import React, { useState, useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { authenticatedFetch, getUserProfile } from '../utils/auth';
import { getUserMapPreference } from '../utils/mapUtils';
import { detectInputType, parseLatLng } from '../utils/locationUtils';
import { useLocationDetection } from '../hooks/useLocationDetection';
import { usePdfGeneration } from '../hooks/usePdfGeneration';
import { handleApiError } from '../utils/errorHandler';
import PdfButton from './common/PdfButton';
import LocationSourceMessage from './LocationSourceMessage';
import { useAddresses } from '../hooks/useAddresses';
import { Link } from 'react-router-dom';
import Modal from './common/Modal';
import DedupControl from './DedupControl';
import DedupTransactionModal from './DedupTransactionModal';
import { generateUnifiedPDF, generateSearchEvidencePDF } from '../utils/pdfGenerator';


// Add Material Icons CSS for better icon support
const materialIconsCSS = document.createElement('link');
materialIconsCSS.rel = 'stylesheet';
materialIconsCSS.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
document.head.appendChild(materialIconsCSS);

// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Pin2PointLeaflet = () => {
  // State variables
  const [input, setInput] = useState('');
  const [result, setResult] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inputHistory, setInputHistory] = useState([]);
  const [usageHistory, setUsageHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputType, setInputType] = useState(''); // 'address' or 'digipin'
  const [copied, setCopied] = useState({});
  const [searchMarker, setSearchMarker] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [user, setUser] = useState(null);
  const [mapType, setMapType] = useState('roadmap');
  const [satelliteLabels, setSatelliteLabels] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showRecentSearchesDropdown, setShowRecentSearchesDropdown] = useState(false);
  const [highlightedRecentIndex, setHighlightedRecentIndex] = useState(-1);
  const [recentSearchTerm, setRecentSearchTerm] = useState("");
  const [showMyLocations, setShowMyLocations] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    input: '',
    label: '',
    inputType: 'address',
    place_id: null,
    setAsDefault: false
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formMessage, setFormMessage] = useState('');

  // Specific Location functionality states
  const [showSpecificLocationField, setShowSpecificLocationField] = useState(false);
  const [specificLocationSearch, setSpecificLocationSearch] = useState("");
  const [specificLocationDropdownOpen, setSpecificLocationDropdownOpen] = useState(false);
  const [highlightedSpecificIndex, setHighlightedSpecificIndex] = useState(-1);
  const specificLocationRef = useRef(null);
  const specificDropdownRef = useRef(null);

  // Default saved location state (for pre-populating search field)
  const [defaultSavedLocation, setDefaultSavedLocation] = useState(null);

  // De-Dup functionality states
  const [enableDedup, setEnableDedup] = useState(false);
  const [dedupLevel, setDedupLevel] = useState(10);
  const [dedupData, setDedupData] = useState(null);
  const [dedupLoading, setDedupLoading] = useState(false);
  const [dedupMarkers, setDedupMarkers] = useState([]);
  const [selectedDedupGroup, setSelectedDedupGroup] = useState(null);
  const [showDedupModal, setShowDedupModal] = useState(false);
  const [dedupRectangles, setDedupRectangles] = useState([]);
  const [dedupRectanglesKey, setDedupRectanglesKey] = useState(0);
  const [addressSimilarityThreshold, setAddressSimilarityThreshold] = useState(80);
  
  // Add state for route result display
  
  // Add state for route path coordinates (for PDF generation)
  const [routePath, setRoutePath] = useState(null);

  // Toast notification states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Toast helper functions
  const showToastMessage = (message, duration = 5000) => {
    setToastMessage(message);
    setShowToast(true);
    
    // Auto-hide after duration
    setTimeout(() => {
      setShowToast(false);
    }, duration);
  };

  const dismissToast = () => {
    setShowToast(false);
  };

  // Route and distance calculation states
  const [distances, setDistances] = useState([]);
  const [newAddressCoords, setNewAddressCoords] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeCalculationType, setRouteCalculationType] = useState('nearest'); // 'nearest' or 'specific'
  const [routeError, setRouteError] = useState('');
  const [routeAddresses, setRouteAddresses] = useState(null);
  const [directions, setDirections] = useState(null);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [mapMarker, setMapMarker] = useState(null);
  const [showRouteInfo, setShowRouteInfo] = useState(false);
  const [showCurrentLocationPopup, setShowCurrentLocationPopup] = useState(true);
  const [mapClickLoading, setMapClickLoading] = useState(false);
  
  // Map layer state
  const [mapLayers, setMapLayers] = useState(null);
  const [currentLayer, setCurrentLayer] = useState(null);
  const [currentLayerName, setCurrentLayerName] = useState('Street Map');
  
  const [boundsKey, setBoundsKey] = useState(0); // Add this to force bounds recalculation
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [preAddrSearch, setPreAddrSearch] = useState("");
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Leaflet map specific states
  const [map, setMap] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const selectedLocationMarkerRef = useRef(null);
  const savedLocationMarkersRef = useRef([]);
  const routeInfoPopupRef = useRef(null);
  const mainSearchRef = useRef(null);
  const recentDropdownRef = useRef(null);

  // Use shared location detection hook
  const {
    userLocation,
    isLocationInIndiaState,
    locationLoading,
    locationSource,
    showLocationMessage,
    defaultMapCenter,
    setShowLocationMessage,
    handleLocationPermissionGranted,
    handleLocationPermissionDenied,
    handleSetDefaultLocation
  } = useLocationDetection();

  // Use PDF generation hook
  const { generatePDF, generateUnifiedPDF, generateDigipinPDF, createSearchRecord, createDigipinSearchRecord, isLoading, error: pdfError } = usePdfGeneration();
  
  // Use the new address management hook
  const {
    addresses,
    loading: addressesLoading,
    error: addressesError,
    refreshAddresses: contextRefreshAddresses
  } = useAddresses();

  // Reusable function to reset map to initial state
  const resetMapToInitialState = useCallback((mapInstance) => {
    if (!mapInstance) return null;
    
    // Use user location if available, otherwise default to India center
    const center = userLocation && userLocation.lat && userLocation.lng 
      ? [userLocation.lat, userLocation.lng] 
      : [20.5937, 78.9629];
    const zoom = userLocation && userLocation.lat && userLocation.lng ? 18 : 5;
    
    console.log('🗺️ Setting map to initial state:', { center, zoom });
    mapInstance.setView(center, zoom);
    
    return { center, zoom };
  }, [userLocation]);

  // Initialize Leaflet map
  useEffect(() => {
    if (locationLoading) {
      console.log('🗺️ Location still loading, waiting...');
      return;
    }

    if (!mapRef.current) {
      console.log('🗺️ mapRef.current is null, returning early');
      return;
    }

    const initializeMap = () => {
      try {
        console.log('🗺️ Attempting to create Leaflet map...');
        console.log('🗺️ mapRef.current:', mapRef.current);
        
        // Ensure the DOM element is fully rendered and has dimensions
        if (!mapRef.current.offsetWidth || !mapRef.current.offsetHeight) {
          console.log('🗺️ Map container has no dimensions yet, retrying...');
          return false;
        }

        // Create Leaflet map with temporary center (will be set to initial state after setup)
        const newMap = L.map(mapRef.current, {
          center: [20.5937, 78.9629], // Temporary center
          zoom: 5 // Temporary zoom
        });

        // Define multiple map tile layers using free alternatives
        const mapLayers = {
          'Street Map': {
            type: 'single',
            base: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
              attribution: '© OpenStreetMap contributors',
              maxZoom: 19
            })
          },
          'Satellite': {
            type: 'single',
            base: L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
              attribution: '© Esri, Maxar, Earthstar Geographics',
              maxZoom: 19
            })
          }
        };

        // Store map layers for custom control immediately
        setMapLayers(mapLayers);

        // Add default layer based on user preference
        let defaultLayerName = 'Street Map';
        let defaultLayer = mapLayers['Street Map'];
        
        // Use satellite if user prefers it
        if (mapType === 'satellite' || mapType === 'hybrid') {
          defaultLayerName = 'Satellite';
          defaultLayer = mapLayers['Satellite'];
        }
        
        // Add the default layer (both street and satellite are now single type)
        if (defaultLayer.base) {
          defaultLayer.base.addTo(newMap);
        }
        
        // Set current layer state
        setCurrentLayer(defaultLayer);
        setCurrentLayerName(defaultLayerName);

        // Disable default zoom controls and add custom ones
        newMap.zoomControl.remove();
        L.control.zoom({
          position: 'bottomright'
        }).addTo(newMap);

        console.log('🗺️ Leaflet map initialized successfully');
        setMap(newMap);
        mapInstanceRef.current = newMap;
        
        // Set map to initial state based on user location
        resetMapToInitialState(newMap);
        
        setMapReady(true);
        return true;
      } catch (error) {
        console.error('❌ Error in initializeMap:', error);
        return false;
      }
    };

    // Try to initialize immediately
    if (!initializeMap()) {
      // If failed, retry with increasing delays
      const retryDelays = [100, 200, 500, 1000];
      let retryCount = 0;
      
      const retryInit = () => {
        if (retryCount < retryDelays.length) {
          setTimeout(() => {
            if (!initializeMap()) {
              retryCount++;
              retryInit();
            }
          }, retryDelays[retryCount]);
        } else {
          console.error('❌ Failed to initialize map after all retries');
        }
      };
      
      retryInit();
    }

    return () => {
      if (map) {
        try {
          map.remove();
        } catch (error) {
          console.warn('Map cleanup warning:', error);
        }
      }
    };
  }, [locationLoading, userLocation, mapType, satelliteLabels]); // Re-run when location loading is complete, user location changes, or map preferences change

  // Add marker to Leaflet map
  const addMarker = async (lat, lng, title, isCurrentMarker = false, isUserLocation = false, digipin = null) => {
    if (!map || !mapReady) {
      console.log('📍 Map not ready yet, skipping marker addition');
      return;
    }
    
    try {
      // Remove existing marker if it's a current marker
      if (isCurrentMarker && markerRef.current) {
        map.removeLayer(markerRef.current);
        markerRef.current = null;
      }

      // Create a custom Leaflet marker icon
      const customIcon = L.divIcon({
        html: isCurrentMarker ? '📍' : '🎯',
        className: 'custom-marker-icon',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });
      
      // Create Leaflet marker
      const marker = L.marker([lat, lng], {
        icon: customIcon,
        title: title
      }).addTo(map);
      
      // Create popup content with buttons
      // Escape single quotes to prevent JavaScript errors
      const safeTitle = title.replace(/'/g, "&#39;");
      const safeDigipin = (digipin || 'N/A').replace(/'/g, "&#39;");
      
      console.log('📍 addMarker called with lat:', lat, 'lng:', lng, 'title:', title);
      
      const popupContent = `
        <div style="padding: 8px; max-width: 250px;">
          <h4 style="margin: 0 0 6px 0; color: #007bff; font-size: 13px; font-weight: bold;">
            ${safeTitle}
          </h4>
          <div style="font-size: 11px; line-height: 1.4; margin-bottom: 8px;">
            <div style="margin-bottom: 4px;"><strong>Address:</strong> ${safeTitle}</div>
            <div style="margin-bottom: 4px;"><strong>Coordinates:</strong> ${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}</div>
            ${digipin && digipin !== 'N/A' ? 
              `<div style="margin-bottom: 4px;"><strong>Digipin:</strong> ${safeDigipin}</div>` : ''}
          </div>
          <div style="display: flex; gap: 4px; justify-content: center; flex-wrap: wrap;">
            ${!isUserLocation ? `
            <button onclick="window.addToMyLocation && window.addToMyLocation({
              lat: ${lat},
              lng: ${lng},
              address: '${safeTitle}',
              digipin: '${safeDigipin}'
            })" 
            style="
              background: #28a745;
              color: white;
              border: none;
              border-radius: 4px;
              padding: 4px 8px;
              font-size: 11px;
              cursor: pointer;
              font-weight: 500;
            ">
              📍 Add to My Location
            </button>
            ` : ''}
            <button onclick="console.log('🎯 Button clicked!'); console.log('🎯 window.handleNearestLocation:', typeof window.handleNearestLocation); if (window.handleNearestLocation) { window.handleNearestLocation(); } else { console.error('🎯 window.handleNearestLocation is not defined!'); }" 
            style="
              background: #007bff;
              color: white;
              border: none;
              border-radius: 4px;
              padding: 4px 8px;
              font-size: 11px;
              cursor: pointer;
              font-weight: 500;
            ">
              🎯 Nearest Location
            </button>
            <button onclick="window.handleMyLocationClick && window.handleMyLocationClick({
              lat: ${lat},
              lng: ${lng},
              address: '${safeTitle}',
              digipin: '${safeDigipin}'
            })" 
            style="
              background: #34A853;
              color: white;
              border: none;
              border-radius: 4px;
              padding: 4px 8px;
              font-size: 11px;
              cursor: pointer;
              font-weight: 500;
            ">
              🏠 My Location
            </button>
            <button onclick="window.handleGeneratePDF && window.handleGeneratePDF({
              lat: ${lat},
              lng: ${lng},
              address: '${safeTitle}',
              digipin: '${safeDigipin}'
            })" 
            style="
              background: #dc3545;
              color: white;
              border: none;
              border-radius: 4px;
              padding: 4px 8px;
              font-size: 11px;
              cursor: pointer;
              font-weight: 500;
            ">
              📄 Generate PDF
            </button>
          </div>
        </div>
      `;
      
      // Always bind popup to make marker clickable
      marker.bindPopup(popupContent);
      
      // Store reference for later removal
      if (isCurrentMarker) {
        markerRef.current = marker;
      }
      
      console.log('📍 Leaflet marker added successfully at:', { lat, lng });
    } catch (error) {
      console.error('❌ Error adding Leaflet marker:', error);
    }
  };

  // Handle map click
  const handleMapClick = async (e) => {
    console.log('🗺️ Leaflet map clicked:', e.latlng);
    
    if (!map || !mapReady) {
      console.log('🗺️ Map not ready, returning early');
      return;
    }
    
    try {
      const { lat, lng } = e.latlng;
      console.log('🗺️ Map clicked:', { lat, lng });
      
      // Reset search box and clear any errors when clicking on map
      setInput('');
      setError('');
      
      // Set map marker state (will be updated with proper address after reverse geocoding)
      setMapMarker({ lat, lng, address: `${lat},${lng}` });
      
      // Add marker first with temporary title
      await addMarker(lat, lng, 'Selected Location', true, false, null);
      
      // Generate Digipin from coordinates (this will also do reverse geocoding)
      await generateDigipinFromCoordinates(lat, lng);
      
    } catch (error) {
      console.error('❌ Error handling map click:', error);
    }
  };

  // Generate digipin from coordinates by calling the backend
  const generateDigipinFromCoordinates = async (lat, lng) => {
    try {
      // First, try to get the address using reverse geocoding
      let address = `${lat},${lng}`; // Fallback to coordinates
      let place_id = null;
      let place_name = '';
      
      try {
        console.log('🗺️ Attempting reverse geocoding for:', { lat, lng });
        const reverseGeocodeResponse = await authenticatedFetch('/reverse-geocode', {
          method: 'POST',
          body: JSON.stringify({ lat, lng }),
        });
        
        if (reverseGeocodeResponse.ok) {
          const reverseData = await reverseGeocodeResponse.json();
          console.log('🗺️ Reverse geocoding successful:', reverseData);
          
          // Use the address from reverse geocoding if available
          if (reverseData.address && reverseData.address.trim() !== '') {
            address = reverseData.address;
            place_id = reverseData.place_id || null;
            place_name = reverseData.place_name || '';
            console.log('🗺️ Using reverse geocoded address:', address);
          } else {
            console.log('🗺️ No address from reverse geocoding, using coordinates');
          }
        } else {
          console.log('🗺️ Reverse geocoding failed, using coordinates');
        }
      } catch (reverseError) {
        console.log('🗺️ Reverse geocoding error, using coordinates:', reverseError);
      }
      
      // Now generate the digipin
      const response = await authenticatedFetch('/generate-digipin', {
        method: 'POST',
        body: JSON.stringify({ 
          lat, 
          lng,
          place_id: place_id,
          place_name: place_name
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Create result object with the proper address
        const result = {
          address: address,
          digipin: data.digipin,
          lat: lat,
          lng: lng,
          place_id: place_id,
          place_name: place_name
        };
        
        setResult(result);
        setLatitude(lat);
        setLongitude(lng);
        setSearchMarker({ lat, lng, address: address, digipin: data.digipin });
        setError('');
        
        // Update map marker with the proper address
        setMapMarker({ lat, lng, address: address, digipin: data.digipin });
        
        // Update input field with the address (not coordinates)
        setInput(address);
        
        console.log('🗺️ Map click result:', result);
        
      } else {
        setError('Failed to generate digipin');
      }
    } catch (error) {
      console.error('Error generating digipin from coordinates:', error);
      setError('Error generating digipin');
    }
  };

  // Generate digipin for search results
  const generateDigipinForSearch = async (lat, lng, address, isAddressSearch = false, originalInput = null) => {
    try {
      // Build request body - only include address if it's a real address (not coordinates)
      const requestBody = {
        lat,
        lng,
        is_address_search: isAddressSearch
      };
      
      // Use original user input if available (what they typed), otherwise use the geocoded address
      // For saving purposes, we prefer the original input over the formatted address
      const addressToSave = originalInput && originalInput !== `${lat},${lng}` ? originalInput : 
                           (address && address !== `${lat},${lng}` ? address : null);
      
      // Only add address if it exists and is not just coordinates
      if (addressToSave) {
        requestBody.address = addressToSave;
      }
      
      console.log('=== generateDigipinForSearch DEBUG ===');
      console.log('Sending to /generate-digipin:', requestBody);
      console.log('isAddressSearch:', isAddressSearch);
      console.log('formatted address (for display):', address);
      console.log('original input (for saving):', originalInput);
      console.log('addressToSave:', addressToSave);
      
      const response = await authenticatedFetch('/generate-digipin', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Create enhanced search marker with digipin
        const enhancedSearchMarker = {
          lat: lat,
          lng: lng,
          address: address || `${lat},${lng}`,
          digipin: data.digipin || 'N/A'
        };
        
        setSearchMarker(enhancedSearchMarker);
        setResult(enhancedSearchMarker);
        setLatitude(lat);
        setLongitude(lng);
        
        // Add marker to map
        await addMarker(lat, lng, address || `${lat},${lng}`, true, false, data.digipin);
        
        // Map will auto-center via useEffect
        
      } else {
        // Fallback if digipin generation fails
        setSearchMarker({ lat, lng, address: address || `${lat},${lng}`, digipin: 'N/A' });
        setLatitude(lat);
        setLongitude(lng);
        
        // Add marker to map
        await addMarker(lat, lng, address || `${lat},${lng}`, true, false, 'N/A');
        
        // Map will auto-center via useEffect
      }
    } catch (error) {
      console.error('Error generating digipin for search:', error);
      // Fallback if API call fails
      setSearchMarker({ lat, lng, address: address || `${lat},${lng}`, digipin: 'N/A' });
      setLatitude(lat);
      setLongitude(lng);
      
      // Add marker to map
      await addMarker(lat, lng, address || `${lat},${lng}`, true, false, 'N/A');
      
      // Reposition map to the search result
      if (map) {
        map.setView([lat, lng], 18);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    const currentInput = input.trim();
    
    setLoading(true);
    setError('');
    setResult('');
    
    // Clear existing search and route markers before new search
    clearSearchAndRouteMarkers();
    
    setInput(currentInput);
    
    try {
      const inputType = detectInputType(input);
      
      if (inputType === 'latlng') {
        const coords = parseLatLng(input);
        generateDigipinForSearch(coords.lat, coords.lng, `${coords.lat},${coords.lng}`);
      } else {
        // For address or digipin, use Google Geocoding API (client-side only)
        if (window.google && window.google.maps) {
          const geocoder = new window.google.maps.Geocoder();
          
          let geocodeRequest;
          if (inputType === 'digipin') {
            try {
              const response = await authenticatedFetch('/get-address', {
                method: 'POST',
                body: JSON.stringify({ digipin: input.trim() }),
              });
              
              if (response.ok) {
                const data = await response.json();
                generateDigipinForSearch(data.lat, data.lng, data.address);
              } else {
                const errorData = await response.json();
                setError(handleApiError(errorData, 'Failed to decode Digipin.'));
                setLoading(false);
              }
            } catch (error) {
              setError('Network error. Please try again.');
              setLoading(false);
            }
            return;
          } else {
            // Append ", India" to address if not already present
            let addressToGeocode = input.trim();
            const lowerAddress = addressToGeocode.toLowerCase();
            // Check if address already ends with "India" or ", India"
            if (!lowerAddress.endsWith('india') && !lowerAddress.endsWith(', india')) {
              addressToGeocode = `${addressToGeocode}, India`;
            }
            geocodeRequest = { address: addressToGeocode };
          }
          
          geocoder.geocode(geocodeRequest, (results, status) => {
            if (status === 'OK' && results[0]) {
              const location = results[0].geometry.location;
              const lat = location.lat();
              const lng = location.lng();
              
              // Pass the original user input (currentInput) instead of formatted address
              // formatted_address is still passed for display purposes, but originalInput is used for saving
              generateDigipinForSearch(lat, lng, results[0].formatted_address, true, currentInput);
            } else {
              setError('Could not find location. Please try a different search term or click on the map.');
              setLoading(false);
            }
          });
        } else {
          setError('Google Maps not loaded. Please refresh the page.');
          setLoading(false);
        }
      }
    } catch (error) {
      setError('Error processing search. Please try again.');
      setLoading(false);
    }
  };

  const clearAll = () => {
    setInput('');
    setResult('');
    setLatitude('');
    setLongitude('');
    setError('');
    setSearchMarker(null);
    setDistances([]);
    setNewAddressCoords(null);
    setRouteError('');
    setRouteLoading(false);
    setRouteAddresses(null);
    setDirections(null);
    setMapMarkers([]);
    setMapMarker(null);
    
    // Clear markers from Leaflet map
    if (markerRef.current && map) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
    
    // Clear selected location marker
    if (selectedLocationMarkerRef.current && map) {
      map.removeLayer(selectedLocationMarkerRef.current);
      selectedLocationMarkerRef.current = null;
    }
    
    // Clear saved location markers, de-dup rectangles/markers, and route polylines
    if (savedLocationMarkersRef.current.length > 0 && map) {
      savedLocationMarkersRef.current.forEach(marker => {
        map.removeLayer(marker);
      });
      savedLocationMarkersRef.current = [];
    }
    
    // Clear specific location dropdown but keep the field visible
    setSpecificLocationDropdownOpen(false);
    setHighlightedSpecificIndex(-1);
    
    // Reset map to initial state using the reusable function
    if (map) {
      resetMapToInitialState(map);
      
      // Explicitly add user location marker if user location is available
      // (since we just cleared markerRef.current, we need to recreate it)
      if (userLocation && userLocation.lat && userLocation.lng) {
        console.log('📍 Explicitly adding user location marker after clear:', userLocation);
        addMarker(userLocation.lat, userLocation.lng, userLocation.address || 'Your Location', true, true, userLocation.digipin);
      }
    }
  };

  // Initialize map click handler
  useEffect(() => {
    if (map && mapReady) {
      console.log('🗺️ Binding Leaflet map click event');
      
      map.off('click');
      map.on('click', handleMapClick);
      
      console.log('🗺️ Leaflet map click event bound successfully');
    }
  }, [map, mapReady]);

  // Add user location marker when user location is available
  useEffect(() => {
    console.log('📍 User location useEffect triggered');
    console.log('📍 map:', map);
    console.log('📍 mapReady:', mapReady);
    console.log('📍 userLocation:', userLocation);
    console.log('📍 locationLoading:', locationLoading);
    console.log('📍 directions:', directions);
    
    // Don't add user location marker if a route is active (to avoid overriding route markers)
    if (map && mapReady && userLocation && userLocation.lat && userLocation.lng && !directions) {
      console.log('📍 Adding user location marker:', userLocation);
      addMarker(userLocation.lat, userLocation.lng, userLocation.address || 'Your Location', true, true, userLocation.digipin);
    } else {
      console.log('📍 Not adding user location marker - conditions not met or route is active');
    }
  }, [map, mapReady, userLocation, directions]);

  // Manage current location popup visibility based on route state
  useEffect(() => {
    if (markerRef.current && map && mapReady) {
      if (directions) {
        // Route is active - close current location popup if it's open
        markerRef.current.closePopup();
      }
    }
    
    // Also manage selected location marker popup visibility
    if (selectedLocationMarkerRef.current && map && mapReady) {
      if (directions) {
        // Route is active - close selected location popup if it's open
        selectedLocationMarkerRef.current.closePopup();
      }
    }
  }, [directions, map, mapReady]);

  // Auto-center map on current marker (similar to Pin2PointMain.js behavior)
  useEffect(() => {
    if (!map || !mapReady) return;

    // Determine the current marker (priority: result > searchMarker > userLocation)
    let currentMarker = null;
    
    if (result && result.lat && result.lng) {
      currentMarker = { lat: result.lat, lng: result.lng };
    } else if (searchMarker && searchMarker.lat && searchMarker.lng) {
      currentMarker = { lat: searchMarker.lat, lng: searchMarker.lng };
    } else if (userLocation && userLocation.lat && userLocation.lng) {
      currentMarker = { lat: userLocation.lat, lng: userLocation.lng };
    }

    // Create bounds to include all relevant markers
    let shouldFitBounds = false;
    const bounds = L.latLngBounds();
    
    // Add current marker to bounds if available
    if (currentMarker) {
      bounds.extend([currentMarker.lat, currentMarker.lng]);
      shouldFitBounds = true;
    }
    
    // Add saved location markers to bounds if shown
    if (showMyLocations && addresses.length > 0) {
      console.log('🗺️ Including saved locations in bounds calculation');
      addresses.forEach(addr => {
        bounds.extend([parseFloat(addr.lat), parseFloat(addr.lng)]);
        shouldFitBounds = true;
      });
    }

    // Apply bounds or center on single marker
    if (shouldFitBounds) {
      console.log('🗺️ Auto-centering map with bounds calculation');
      setTimeout(() => {
        if (bounds.isValid()) {
          if (showMyLocations && addresses.length > 0) {
            // When showing saved locations, fit bounds to include all markers
            map.fitBounds(bounds, { padding: [20, 20] });
          } else if (directions && directions.routes && directions.routes.length > 0) {
            // When a route is active, maintain route bounds instead of centering on current marker
            console.log('🗺️ Route is active, maintaining route bounds');
            const route = directions.routes[0];
            const leg = route.legs[0];
            const routeCoordinates = [];
            
            // Extract route coordinates for bounds calculation
            routeCoordinates.push([leg.start_location.lat(), leg.start_location.lng()]);
            leg.steps.forEach(step => {
              const encodedPolyline = step.polyline.points;
              const decodedPath = window.google.maps.geometry.encoding.decodePath(encodedPolyline);
              decodedPath.forEach(point => {
                routeCoordinates.push([point.lat(), point.lng()]);
              });
            });
            routeCoordinates.push([leg.end_location.lat(), leg.end_location.lng()]);
            
            // Fit bounds to show the entire route
            const routeBounds = L.latLngBounds(routeCoordinates);
            map.fitBounds(routeBounds, { padding: [20, 20] });
          } else if (currentMarker) {
            // When not showing saved locations and no route, center on current marker
            map.setView([currentMarker.lat, currentMarker.lng], 18);
          }
        } else if (currentMarker) {
          // Fallback to centering on current marker
          map.setView([currentMarker.lat, currentMarker.lng], 18);
        }
        map.invalidateSize();
      }, 100);
    }
  }, [result, searchMarker, userLocation, map, mapReady, showMyLocations, addresses, directions]);


  // Add saved locations markers when checkbox is checked
  useEffect(() => {
    if (map && mapReady) {
      // Clear ONLY existing saved location markers (not other types)
      savedLocationMarkersRef.current.forEach(marker => {
        if (marker.isSavedLocationMarker) {
          map.removeLayer(marker);
        }
      });
      // Remove only saved location markers from the array
      savedLocationMarkersRef.current = savedLocationMarkersRef.current.filter(marker => !marker.isSavedLocationMarker);

      if (showMyLocations && addresses.length > 0) {
        console.log('📍 Adding saved locations markers:', addresses);
        addresses.forEach((addr, index) => {
          // Use a different marker for saved locations
          const customIcon = L.divIcon({
            html: '🏠',
            className: 'custom-marker-icon',
            iconSize: [32, 32],
            iconAnchor: [16, 32],
            popupAnchor: [0, -32]
          });
          
          const marker = L.marker([parseFloat(addr.lat), parseFloat(addr.lng)], {
            icon: customIcon,
            title: addr.label || addr.address
          }).addTo(map);
          
          marker.bindPopup(`
            <div>
              <strong>${addr.label || addr.address}</strong><br/>
              <small>${addr.address}</small><br/>
              <small>Digipin: ${addr.digipin || 'N/A'}</small>
            </div>
          `);
          
          // Mark as saved location marker for cleanup
          marker.isSavedLocationMarker = true;
          savedLocationMarkersRef.current.push(marker);
        });
      }

      // Clear existing De-Dup rectangles and markers before adding new ones
      // This ensures old rectangles/markers are removed when dedupLevel changes
      savedLocationMarkersRef.current.forEach(marker => {
        if (marker.isDedupRectangle || marker.isDedupMarker) {
          map.removeLayer(marker);
        }
      });
      // Remove de-dup rectangles and markers from the array
      savedLocationMarkersRef.current = savedLocationMarkersRef.current.filter(
        marker => !marker.isDedupRectangle && !marker.isDedupMarker
      );

      // Add De-Dup rectangles and markers if enabled
      if (enableDedup && dedupRectangles.length > 0) {
        console.log('Adding De-Dup rectangles to map:', dedupRectangles.length);
        
        // Add rectangles first
        dedupRectangles.forEach(rect => {
          if (rect.bounds && rect.bounds.north > rect.bounds.south && rect.bounds.east > rect.bounds.west) {
            // Determine color based on count
            const count = rect.count || 0;
            let fillColor, strokeColor;
            if (count === 0) {
              fillColor = '#28a745'; // Green
              strokeColor = '#28a745';
            } else if (count >= 1 && count < 5) {
              fillColor = '#fd7e14'; // Orange
              strokeColor = '#fd7e14';
            } else if (count >= 5 && count < 10) {
              fillColor = '#6f42c1'; // Purple
              strokeColor = '#6f42c1';
            } else {
              fillColor = '#dc3545'; // Red (10+ searches)
              strokeColor = '#dc3545';
            }
            
            const rectangle = L.rectangle([
              [rect.bounds.north, rect.bounds.west],
              [rect.bounds.south, rect.bounds.east]
            ], {
              color: strokeColor,
              fillColor: fillColor,
              fillOpacity: 0.4,
              weight: 5,
              opacity: 1.0,
              clickable: false
            }).addTo(map);
            
            // Mark as de-dup rectangle for cleanup
            rectangle.isDedupRectangle = true;
            savedLocationMarkersRef.current.push(rectangle);
          }
        });
      }
      
      // Add De-Dup markers if enabled
      if (enableDedup && dedupMarkers.length > 0) {
        console.log('Adding De-Dup markers to map:', dedupMarkers.length);
        dedupMarkers.forEach(marker => {
          // Create custom icon with count display (similar to Google Maps version)
          const count = marker.count || 0;
          let markerColor;
          if (count === 0) markerColor = '#28a745'; // Green
          else if (count >= 1 && count < 5) markerColor = '#fd7e14'; // Orange
          else if (count >= 5 && count < 10) markerColor = '#6f42c1'; // Purple
          else markerColor = '#dc3545'; // Red (10+ searches)
          
          const countText = count > 99 ? '99+' : count.toString();
          
          const customIcon = L.divIcon({
            html: `
              <div style="
                width: 32px;
                height: 32px;
                background-color: ${markerColor};
                border: 2px solid white;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                font-family: Arial, sans-serif;
                font-size: 10px;
                font-weight: bold;
                color: white;
                text-align: center;
                line-height: 1;
              ">
                ${countText}
              </div>
            `,
            className: 'custom-dedup-marker-icon',
            iconSize: [32, 32],
            iconAnchor: [16, 16],
            popupAnchor: [0, -16]
          });
          
          // Position marker slightly to the left of rectangle to avoid click conflicts
          const rect = dedupRectangles && dedupRectangles.length > 0 ? dedupRectangles[0] : null;
          let markerPosition = [marker.lat, marker.lng];
          
          if (rect && rect.bounds) {
            // Move marker slightly to the left of the rectangle
            const offset = 0.0001; // Small offset to the left
            markerPosition = [rect.bounds.north, rect.bounds.west - offset];
          }
          
          const leafletMarker = L.marker(markerPosition, {
            icon: customIcon,
            title: `De-Dup: ${marker.count} searches (Level ${marker.dedupLevel})`
          }).addTo(map);
          
          leafletMarker.bindPopup(`
            <div>
              <strong>De-Dup Group: ${marker.digipin_prefix}</strong><br/>
              <small>Count: ${marker.count}</small><br/>
              <small>Level: ${marker.dedupLevel}</small>
            </div>
          `);
          
          leafletMarker.on('click', () => handleDedupMarkerClick(marker));
          
          // Mark as de-dup marker for cleanup
          leafletMarker.isDedupMarker = true;
          savedLocationMarkersRef.current.push(leafletMarker);
        });
      }
    }
  }, [map, mapReady, showMyLocations, addresses, enableDedup, dedupMarkers, dedupRectangles]);

  // Add route markers when mapMarkers state changes
  useEffect(() => {
    if (map && mapReady && mapMarkers.length > 0) {
      console.log('📍 Adding route markers:', mapMarkers);
      
      // Clear existing route markers
      savedLocationMarkersRef.current.forEach(marker => {
        if (marker.isRouteMarker) {
          map.removeLayer(marker);
        }
      });
      savedLocationMarkersRef.current = savedLocationMarkersRef.current.filter(marker => !marker.isRouteMarker);
      
      // Add new route markers
      mapMarkers.forEach(markerData => {
        const customIcon = L.divIcon({
          html: '🎯',
          className: 'custom-marker-icon',
          iconSize: [32, 32],
          iconAnchor: [16, 32],
          popupAnchor: [0, -32]
        });
        
        const marker = L.marker([markerData.lat, markerData.lng], {
          icon: customIcon,
          title: markerData.label || markerData.address
        }).addTo(map);
        
        // Debug route marker data
        console.log('📍 Creating route marker popup for:', markerData);
        
        // Ensure coordinates are valid numbers
        const safeLat = parseFloat(markerData.lat);
        const safeLng = parseFloat(markerData.lng);
        const formattedLat = !isNaN(safeLat) ? safeLat.toFixed(6) : 'N/A';
        const formattedLng = !isNaN(safeLng) ? safeLng.toFixed(6) : 'N/A';
        
        marker.bindPopup(`
          <div style="padding: 8px; max-width: 250px;">
            <h4 style="margin: 0 0 6px 0; color: #28a745; font-size: 13px; font-weight: bold;">
              ${markerData.label || markerData.address}
            </h4>
            <div style="font-size: 11px; line-height: 1.4; margin-bottom: 8px;">
              <div style="margin-bottom: 4px;"><strong>Address:</strong> ${markerData.address || markerData.label}</div>
              <div style="margin-bottom: 4px;"><strong>Coordinates:</strong> ${formattedLat}, ${formattedLng}</div>
              <div style="margin-bottom: 4px;"><strong>Digipin:</strong> ${markerData.digipin || 'N/A'}</div>
            </div>
          </div>
        `);
        
        // Mark as route marker for cleanup
        marker.isRouteMarker = true;
        savedLocationMarkersRef.current.push(marker);
      });
    }
  }, [map, mapReady, mapMarkers]);

  // Manage selected location marker (from mapMarker or searchMarker)
  useEffect(() => {
    if (map && mapReady) {
      // Remove existing selected location marker
      if (selectedLocationMarkerRef.current) {
        map.removeLayer(selectedLocationMarkerRef.current);
        selectedLocationMarkerRef.current = null;
      }
      
      // Determine the selected marker (priority: result > mapMarker > searchMarker)
      let selectedMarker = null;
      
      if (result && result.lat && result.lng) {
        selectedMarker = { 
          lat: result.lat, 
          lng: result.lng, 
          address: result.address || input || `${result.lat},${result.lng}`,
          digipin: result.digipin || 'N/A',
          title: "Selected Location"
        };
      } else if (mapMarker && mapMarker.lat && mapMarker.lng) {
        selectedMarker = { 
          lat: mapMarker.lat, 
          lng: mapMarker.lng, 
          address: mapMarker.address || input || `${mapMarker.lat},${mapMarker.lng}`,
          digipin: mapMarker.digipin || 'N/A',
          title: "Selected Location"
        };
      } else if (searchMarker && searchMarker.lat && searchMarker.lng) {
        selectedMarker = { 
          lat: searchMarker.lat, 
          lng: searchMarker.lng, 
          address: searchMarker.address || input || `${searchMarker.lat},${searchMarker.lng}`,
          digipin: searchMarker.digipin || 'N/A',
          title: "Search Result"
        };
      }
      
      // If we have a selected marker, create and display it
      if (selectedMarker) {
        console.log('📍 Creating selected location marker:', selectedMarker);
        console.log('📍 Marker coordinates - lat:', selectedMarker.lat, 'lng:', selectedMarker.lng);
        console.log('📍 Marker lat type:', typeof selectedMarker.lat, 'lng type:', typeof selectedMarker.lng);
        
        // Create a custom Leaflet marker icon
        const customIcon = L.divIcon({
          html: '📍',
          className: 'custom-marker-icon',
          iconSize: [32, 32],
          iconAnchor: [16, 32],
          popupAnchor: [0, -32]
        });
        
        // Create Leaflet marker
        const marker = L.marker([selectedMarker.lat, selectedMarker.lng], {
          icon: customIcon,
          title: selectedMarker.address
        }).addTo(map);
        
        // Create popup content with buttons - using enhanced marker data
        // Escape single quotes in address to prevent JavaScript errors
        const safeAddress = selectedMarker.address.replace(/'/g, "&#39;");
        const safeTitle = (selectedMarker.title || selectedMarker.address).replace(/'/g, "&#39;");
        const safeDigipin = (selectedMarker.digipin || 'N/A').replace(/'/g, "&#39;");
        
        // Debug coordinates before creating popup
        console.log('📍 About to create popup - lat:', selectedMarker.lat, 'lng:', selectedMarker.lng);
        
        // Ensure coordinates are valid numbers
        const safeLat = parseFloat(selectedMarker.lat);
        const safeLng = parseFloat(selectedMarker.lng);
        console.log('📍 Parsed coordinates - lat:', safeLat, 'lng:', safeLng);
        console.log('📍 Are coordinates valid?', !isNaN(safeLat), !isNaN(safeLng));
        
        // Format coordinates with fallback
        const formattedLat = !isNaN(safeLat) ? safeLat.toFixed(6) : 'N/A';
        const formattedLng = !isNaN(safeLng) ? safeLng.toFixed(6) : 'N/A';
        console.log('📍 Formatted coordinates:', formattedLat, formattedLng);
        
        const popupContent = `
          <div style="padding: 8px; max-width: 250px;">
            <h4 style="margin: 0 0 6px 0; color: #007bff; font-size: 13px; font-weight: bold;">
              ${safeTitle}
            </h4>
            <div style="font-size: 11px; line-height: 1.4; margin-bottom: 8px;">
              <div style="margin-bottom: 4px;"><strong>Address:</strong> ${safeAddress}</div>
              <div style="margin-bottom: 4px;"><strong>Coordinates:</strong> ${formattedLat}, ${formattedLng}</div>
              ${selectedMarker.digipin && selectedMarker.digipin !== 'N/A' ? 
                `<div style="margin-bottom: 4px;"><strong>Digipin:</strong> ${safeDigipin}</div>` : ''}
            </div>
            <div style="display: flex; gap: 4px; justify-content: center; flex-wrap: wrap;">
              <button onclick="window.addToMyLocation && window.addToMyLocation({
                lat: ${selectedMarker.lat},
                lng: ${selectedMarker.lng},
                address: '${safeAddress}',
                digipin: '${safeDigipin}'
              })" 
              style="
                background: #28a745;
                color: white;
                border: none;
                border-radius: 4px;
                padding: 4px 8px;
                font-size: 11px;
                cursor: pointer;
                font-weight: 500;
              ">
                📍 Add to My Location
              </button>
              <button onclick="console.log('🎯 Button clicked!'); console.log('🎯 window.handleNearestLocation:', typeof window.handleNearestLocation); if (window.handleNearestLocation) { window.handleNearestLocation(); } else { console.error('🎯 window.handleNearestLocation is not defined!'); }" 
              style="
                background: #007bff;
                color: white;
                border: none;
                border-radius: 4px;
                padding: 4px 8px;
                font-size: 11px;
                cursor: pointer;
                font-weight: 500;
              ">
                🎯 Nearest Location
              </button>
              <button onclick="window.handleMyLocationClick && window.handleMyLocationClick({
                lat: ${selectedMarker.lat},
                lng: ${selectedMarker.lng},
                address: '${safeAddress}',
                digipin: '${safeDigipin}'
              })" 
              style="
                background: #34A853;
                color: white;
                border: none;
                border-radius: 4px;
                padding: 4px 8px;
                font-size: 11px;
                cursor: pointer;
                font-weight: 500;
              ">
                🏠 My Location
              </button>
              <button onclick="window.handleGeneratePDF && window.handleGeneratePDF({
                lat: ${selectedMarker.lat},
                lng: ${selectedMarker.lng},
                address: '${safeAddress}',
                digipin: '${safeDigipin}'
              })" 
              style="
                background: #dc3545;
                color: white;
                border: none;
                border-radius: 4px;
                padding: 4px 8px;
                font-size: 11px;
                cursor: pointer;
                font-weight: 500;
              ">
                📄 Generate PDF
              </button>
            </div>
          </div>
        `;
        
        console.log('📍 Generated popup content:', popupContent);
        
        // Bind popup
        marker.bindPopup(popupContent);
        
        // Store reference
        selectedLocationMarkerRef.current = marker;
        
        console.log('📍 Selected location marker created successfully');
      }
    }
  }, [map, mapReady, result, mapMarker, searchMarker, input]);

  // Render route polyline when directions change
  useEffect(() => {
    if (map && mapReady && directions && directions.routes && directions.routes.length > 0) {
      console.log('🛣️ Rendering route on Leaflet map');
      
      // Clear existing route polylines and route info popup
      savedLocationMarkersRef.current.forEach(marker => {
        if (marker.isRoutePolyline) {
          map.removeLayer(marker);
        }
      });
      savedLocationMarkersRef.current = savedLocationMarkersRef.current.filter(marker => !marker.isRoutePolyline);
      
      // Clear existing route info popup
      if (routeInfoPopupRef.current) {
        map.removeLayer(routeInfoPopupRef.current);
        routeInfoPopupRef.current = null;
      }
      
      // Get the route from Google Directions result
      const route = directions.routes[0];
      const leg = route.legs[0];
      
      // Extract coordinates from the route steps
      const routeCoordinates = [];
      
      // Add start point
      routeCoordinates.push([
        leg.start_location.lat(),
        leg.start_location.lng()
      ]);
      
      // Add all steps in the route
      leg.steps.forEach(step => {
        const encodedPolyline = step.polyline.points;
        const decodedPath = window.google.maps.geometry.encoding.decodePath(encodedPolyline);
        
        decodedPath.forEach(point => {
          routeCoordinates.push([point.lat(), point.lng()]);
        });
      });
      
      // Add end point
      routeCoordinates.push([
        leg.end_location.lat(),
        leg.end_location.lng()
      ]);
      
      // Store route path for PDF generation
      setRoutePath(routeCoordinates);
      console.log('🛣️ Route path stored for PDF generation:', routeCoordinates.length, 'points');
      
      // Create Leaflet polyline
      const routePolyline = L.polyline(routeCoordinates, {
        color: '#007bff',
        weight: 4,
        opacity: 0.8,
        smoothFactor: 1
      }).addTo(map);
      
      // Mark as route polyline for cleanup
      routePolyline.isRoutePolyline = true;
      savedLocationMarkersRef.current.push(routePolyline);
      
      // Add route info marker at the midpoint
      const routeInfo = getRouteDistanceInfo();
      if (routeInfo && routeInfo.position) {
        // Show route marker icon
        const routeIcon = L.divIcon({
          html: `
            <div style="
              width: 32px;
              height: 32px;
              background-color: #34A853;
              border: 2px solid #ffffff;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
              cursor: pointer;
            ">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                <path d="M12 6v6l4 2" stroke="white" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          `,
          className: 'route-marker-icon',
          iconSize: [32, 32],
          iconAnchor: [16, 32],
          popupAnchor: [0, -32]
        });
        
        const routeMarker = L.marker([routeInfo.position.lat, routeInfo.position.lng], {
          icon: routeIcon,
          title: 'Route Information'
        }).addTo(map);
        
        // Add popup with route info
        const routePopupContent = `
          <div style="padding: 8px; max-width: 220px;">
            <h4 style="margin: 0 0 6px 0; color: #34A853; font-size: 13px; font-weight: bold;">
              Route Information
            </h4>
            <div style="font-size: 11px; line-height: 1.3; margin-bottom: 8px;">
              <div><strong>Road Distance:</strong> ${routeInfo.roadDistance ? `${parseFloat(routeInfo.roadDistance).toFixed(2)} km` : 'N/A'}</div>
              <div><strong>Road Duration:</strong> ${routeInfo.roadDuration || 'N/A'}</div>
              <div><strong>Aerial Distance:</strong> ${routeInfo.aerialDistance ? `${parseFloat(routeInfo.aerialDistance).toFixed(2)} km` : 'N/A'}</div>
            </div>
            <div style="text-align: center; margin-top: 8px;">
              <button onclick="window.generateRouteMultiLocationPDF && window.generateRouteMultiLocationPDF()" 
              style="
                background: #dc3545;
                color: white;
                border: none;
                border-radius: 4px;
                padding: 6px 12px;
                font-size: 11px;
                cursor: pointer;
                font-weight: 500;
              ">
                📄 Generate Route PDF
              </button>
            </div>
          </div>
        `;
        
        routeMarker.bindPopup(routePopupContent);
        
        routeInfoPopupRef.current = routeMarker;
        console.log('🛣️ Route marker added to map');
      }

      
      // Fit map to show the entire route
      const bounds = L.latLngBounds(routeCoordinates);
      map.fitBounds(bounds, { padding: [20, 20] });
      
      console.log('🛣️ Route rendered successfully with', routeCoordinates.length, 'points');
    }
  }, [map, mapReady, directions, showRouteInfo]);

  // Initialize component
  useEffect(() => {
    fetchInputHistory();
    
    const fetchUserProfile = async () => {
      try {
        const profile = await getUserProfile();
        if (profile && profile.email) {
          setUserEmail(profile.email);
          setUser(profile);
        }
      } catch (error) {
        // Error handling
      }
    };
    fetchUserProfile();

    // Fetch user's map preference
    const fetchMapPreference = async () => {
      try {
        const preference = await getUserMapPreference();
        setMapType(preference.map_view_preference);
        setSatelliteLabels(preference.satellite_labels);
        
        // Update current layer name based on preference
        if (preference.map_view_preference === 'satellite' || preference.map_view_preference === 'hybrid') {
          setCurrentLayerName('Satellite');
        } else {
          setCurrentLayerName('Street Map');
        }
      } catch (error) {
        // Error handling - use defaults
        setMapType('roadmap');
        setSatelliteLabels(true);
        setCurrentLayerName('Street Map');
      }
    };
    fetchMapPreference();
    
    // Fetch default saved location to pre-populate search field
    fetchDefaultSavedLocation();
    
  }, []);


  // Fetch addresses when checkbox is checked
  useEffect(() => {
    if (showMyLocations && addresses.length === 0 && !addressesLoading) {
      contextRefreshAddresses();
    }
  }, [showMyLocations, addresses.length, addressesLoading, contextRefreshAddresses]);

  // Fetch De-Dup data when settings change or search result changes
  useEffect(() => {
    console.log('useEffect triggered - enableDedup:', enableDedup, 'dedupLevel:', dedupLevel, 'addressSimilarityThreshold:', addressSimilarityThreshold);
    fetchDedupData();
  }, [enableDedup, dedupLevel, addressSimilarityThreshold, result, searchMarker]);

  // Fetch default saved location to pre-populate search field
  const fetchDefaultSavedLocation = async () => {
    try {
      const response = await authenticatedFetch('/addresses/default');
      if (response.ok) {
        const data = await response.json();
        setDefaultSavedLocation(data.default_address);
        
        // If there's a default saved location, pre-populate the search field
        if (data.default_address) {
          setSpecificLocationSearch(data.default_address.label || data.default_address.address);
        }
      }
    } catch (error) {
      console.error('Error fetching default saved location:', error);
    }
  };

  // Filter specific location addresses
  const filteredSpecificAddresses = addresses.filter(addr => {
    const searchTerm = specificLocationSearch.toLowerCase();
    const addrLabel = (addr.label || '').toLowerCase();
    const addrAddress = (addr.address || '').toLowerCase();
    
    return addrLabel.includes(searchTerm) || addrAddress.includes(searchTerm);
  }).slice(0, 10);

  // Handle specific location selection
  const selectSpecificLocation = (addr) => {
    console.log('selectSpecificLocation called with:', addr);
    console.log('Setting specificLocationSearch to:', addr.address);
    setSpecificLocationSearch(addr.address);
    setSpecificLocationDropdownOpen(false);
    setHighlightedSpecificIndex(-1);
    console.log('Specific location selected successfully');
  };

  // Handle specific location input key events
  const handleSpecificLocationKeyDown = (e) => {
    if (specificLocationDropdownOpen && filteredSpecificAddresses.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedSpecificIndex((prev) => (prev + 1) % filteredSpecificAddresses.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedSpecificIndex((prev) => (prev - 1 + filteredSpecificAddresses.length) % filteredSpecificAddresses.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedSpecificIndex >= 0 && highlightedSpecificIndex < filteredSpecificAddresses.length) {
          selectSpecificLocation(filteredSpecificAddresses[highlightedSpecificIndex]);
        }
      } else if (e.key === 'Escape') {
        setSpecificLocationDropdownOpen(false);
        setHighlightedSpecificIndex(-1);
      }
    }
  };

  // Handle click outside to close specific location dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        specificDropdownRef.current &&
        !specificDropdownRef.current.contains(event.target) &&
        specificLocationRef.current &&
        !specificLocationRef.current.contains(event.target)
      ) {
        setSpecificLocationDropdownOpen(false);
        setHighlightedSpecificIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch De-Dup data
  const fetchDedupData = async () => {
    console.log('fetchDedupData called with enableDedup:', enableDedup, 'dedupLevel:', dedupLevel);

    // Clear rectangles immediately using pure state
    setDedupRectangles([]);
    setDedupRectanglesKey(prev => prev + 1);
    
    // Clear existing data
    setDedupData(null);
    setDedupMarkers([]);
    setSelectedDedupGroup(null);
    setShowDedupModal(false);
    
    if (!enableDedup) {
      console.log('Dedup is disabled, returning early');
      return;
    }

    // Get the current search's DigiPin as reference
    let referenceDigipin = null;
    
    if (result && result.digipin) {
      referenceDigipin = result.digipin;
    } else if (searchMarker && searchMarker.digipin) {
      referenceDigipin = searchMarker.digipin;
    } else if (result && result.lat && result.lng) {
      // Fallback: Generate DigiPin from coordinates if available
      try {
        const response = await authenticatedFetch('/generate-digipin', {
          method: 'POST',
          body: JSON.stringify({ 
            lat: result.lat, 
            lng: result.lng
          }),
        });
        
        if (response.ok) {
          const data = await response.json();
          referenceDigipin = data.digipin;
        }
      } catch (error) {
        console.error('Error generating DigiPin for De-Dup:', error);
      }
    }

    // If no reference DigiPin available, don't fetch data
    if (!referenceDigipin) {
      setDedupData(null);
      setDedupMarkers([]);
      return;
    }

    // Get the current search's address as reference for similarity matching
    let referenceAddress = null;
    if (result && result.address) {
      referenceAddress = result.address;
    } else if (searchMarker && searchMarker.address) {
      referenceAddress = searchMarker.address;
    }

    setDedupLoading(true);
    try {
      // Build query string with parameters
      const queryParams = new URLSearchParams({
        level: dedupLevel.toString(),
        reference_digipin: encodeURIComponent(referenceDigipin)
      });
      
      // Add address filtering if enabled (threshold > 0) and reference address available
      if (addressSimilarityThreshold > 0 && referenceAddress) {
        queryParams.append('reference_address', encodeURIComponent(referenceAddress));
        queryParams.append('address_similarity_threshold', addressSimilarityThreshold.toString());
        console.log('Fetching De-Dup data with address filtering:', { 
          level: dedupLevel, 
          referenceDigipin,
          referenceAddress,
          addressSimilarityThreshold 
        });
      } else {
        console.log('Fetching De-Dup data with:', { level: dedupLevel, referenceDigipin });
      }
      
      const response = await authenticatedFetch(`/dedup-data?${queryParams.toString()}`);
      
      console.log('De-Dup API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('De-Dup API response data:', data);
        setDedupData(data);
        
        // Convert groups to map markers
        const markers = data.groups
          .filter(group => group.center_lat && group.center_lng) // Only include groups with valid coordinates
          .map(group => ({
            lat: group.center_lat,
            lng: group.center_lng,
            count: group.count,
            digipin_prefix: group.digipin_prefix,
            groupData: group,
            bounds: group.bounds,
            dedupLevel: dedupLevel,
            type: 'dedup'
          }));
        console.log('De-Dup markers created:', markers.map(marker => ({
          lat: marker.lat,
          lng: marker.lng,
          count: marker.count,
          digipin_prefix: marker.digipin_prefix,
          bounds: marker.bounds
        })));
        setDedupMarkers(markers);
        
        // Set single rectangle in pure state (should only be one now)
        const rectangles = data.groups
          .filter(group => group.bounds) // Only include groups with valid bounds
          .map(group => ({
            bounds: group.bounds,
            level: dedupLevel,
            count: group.count,
            digipin_prefix: group.digipin_prefix,
            center_lat: group.center_lat,
            center_lng: group.center_lng
          }));
        console.log('Setting single rectangle:', rectangles);
        setDedupRectangles(rectangles);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch de-duplication data:', response.status, errorText);
        setDedupData(null);
        setDedupMarkers([]);
        setDedupRectangles([]);
      }
    } catch (error) {
      console.error('Error fetching de-duplication data:', error);
      setDedupData(null);
      setDedupMarkers([]);
      setDedupRectangles([]);
    } finally {
      setDedupLoading(false);
    }
  };

  // Handle De-Dup marker click
  const handleDedupMarkerClick = (marker) => {
    setSelectedDedupGroup(marker.groupData);
    setShowDedupModal(true);
  };

  // Haversine formula to calculate distance between two points
  const calculateHaversineDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in kilometers
  };

  // Calculate midpoint between two coordinates
  const calculateMidpoint = (lat1, lng1, lat2, lng2) => {
    const midLat = (lat1 + lat2) / 2;
    const midLng = (lng1 + lng2) / 2;
    return { lat: midLat, lng: midLng };
  };

  // Get true midpoint along the actual route path
  const getRoutePathMidpoint = (routeCoordinates) => {
    if (!routeCoordinates || routeCoordinates.length === 0) return null;
    
    // Find the middle point in the route path
    const midIndex = Math.floor(routeCoordinates.length / 2);
    const midPoint = routeCoordinates[midIndex];
    
    return {
      lat: midPoint[0],
      lng: midPoint[1]
    };
  };

  // Generate route distance info for display on map
  const getRouteDistanceInfo = () => {
    if (distances.length === 0) return null;
    
    const nearestLocation = distances[0];
    if (!nearestLocation || !nearestLocation.lat || !nearestLocation.lng) return null;
    
    // Determine which location data to use (priority: result > mapMarker > searchMarker > userLocation)
    let currentLocation = null;
    
    if (result && result.lat && result.lng) {
      currentLocation = result;
    } else if (mapMarker && mapMarker.lat && mapMarker.lng) {
      currentLocation = mapMarker;
    } else if (searchMarker && searchMarker.lat && searchMarker.lng) {
      currentLocation = searchMarker;
    } else if (userLocation && userLocation.lat && userLocation.lng) {
      currentLocation = userLocation;
    } else {
      return null;
    }
    
    // If we have directions data, use route-aware positioning
    if (directions && directions.routes && directions.routes.length > 0) {
      const route = directions.routes[0];
      const leg = route.legs[0];
      
      // Extract coordinates from the route steps
      const routeCoordinates = [];
      
      // Add start point
      routeCoordinates.push([
        leg.start_location.lat(),
        leg.start_location.lng()
      ]);
      
      // Add all steps in the route
      leg.steps.forEach(step => {
        const encodedPolyline = step.polyline.points;
        const decodedPath = window.google.maps.geometry.encoding.decodePath(encodedPolyline);
        
        decodedPath.forEach(point => {
          routeCoordinates.push([point.lat(), point.lng()]);
        });
      });
      
      // Add end point
      routeCoordinates.push([
        leg.end_location.lat(),
        leg.end_location.lng()
      ]);
      
      // Get true midpoint along route path
      const routeMidpoint = getRoutePathMidpoint(routeCoordinates);
      if (routeMidpoint) {
        return {
          position: routeMidpoint,
          roadDistance: nearestLocation.road_distance_km,
          aerialDistance: nearestLocation.aerial_distance_km,
          roadDuration: nearestLocation.road_duration
        };
      }
    }
    
    // Fallback to simple midpoint calculation
    const midpoint = calculateMidpoint(
      currentLocation.lat, currentLocation.lng,
      nearestLocation.lat, nearestLocation.lng
    );
    
    return {
      position: midpoint,
      roadDistance: nearestLocation.road_distance_km,
      aerialDistance: nearestLocation.aerial_distance_km,
      roadDuration: nearestLocation.road_duration
    };
  };

  // Handle route info popup close
  const handleRouteInfoClose = () => {
    setShowRouteInfo(false);
  };

  // Clear route data
  const clearRoute = () => {
    setDirections(null);
    setRouteAddresses(null);
    setShowRouteInfo(false);
    setRoutePath(null); // Clear route path for PDF generation
    
    // Clear route info popup if it exists
    if (routeInfoPopupRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(routeInfoPopupRef.current);
      routeInfoPopupRef.current = null;
    }
    setMapMarkers([]);
    
    // Clear route polylines from map
    if (map) {
      savedLocationMarkersRef.current.forEach(marker => {
        if (marker.isRoutePolyline) {
          map.removeLayer(marker);
        }
      });
      savedLocationMarkersRef.current = savedLocationMarkersRef.current.filter(marker => !marker.isRoutePolyline);
    }
  };

  // Clear all markers except user location and saved locations
  const clearSearchAndRouteMarkers = () => {
    console.log('🧹 Clearing search and route markers...');
    
    // Clear search marker from map (but keep the state for route calculation)
    if (markerRef.current && map) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
    
    // Clear selected location marker from map (but keep the state for route calculation)
    if (selectedLocationMarkerRef.current && map) {
      map.removeLayer(selectedLocationMarkerRef.current);
      selectedLocationMarkerRef.current = null;
    }
    
    // Clear route-related markers (but keep saved location markers)
    if (map) {
      savedLocationMarkersRef.current.forEach(marker => {
        if (marker.isRouteMarker || marker.isRoutePolyline) {
          map.removeLayer(marker);
        }
      });
      savedLocationMarkersRef.current = savedLocationMarkersRef.current.filter(marker => 
        !marker.isRouteMarker && !marker.isRoutePolyline
      );
    }
    
    // Clear route info popup if it exists
    if (routeInfoPopupRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(routeInfoPopupRef.current);
      routeInfoPopupRef.current = null;
    }
    
    
    console.log('🧹 Search and route markers cleared (state preserved for route calculation)');
  };

  // Calculate route using Google Directions API
  const calculateRoute = async (origin, destination, customAddresses = null) => {
    if (!origin || !destination) return;
    
    try {
      const request = {
        origin: origin,
        destination: destination,
        travelMode: window.google.maps.TravelMode.DRIVING
      };
      
      const directionsService = new window.google.maps.DirectionsService();
      const result = await directionsService.route(request);
      
      if (result.status === 'OK' && result.routes && result.routes.length > 0) {
        setDirections(result);
        if (customAddresses) {
          setRouteAddresses(customAddresses);
        }
      }
    } catch (error) {
      console.error('Error calculating route:', error);
    }
  };

  // Calculate nearest location using mathematical formula
  const calculateNearestLocationMathematical = async () => {
    console.log('🎯 calculateNearestLocationMathematical called');
    console.log('🎯 Current state values:');
    console.log('  - result:', result);
    console.log('  - mapMarker:', mapMarker);
    console.log('  - searchMarker:', searchMarker);
    console.log('  - userLocation:', userLocation);
    console.log('🎯 State types and validity:');
    console.log('  - result valid:', result && result.lat && result.lng);
    console.log('  - mapMarker valid:', mapMarker && mapMarker.lat && mapMarker.lng);
    console.log('  - searchMarker valid:', searchMarker && searchMarker.lat && searchMarker.lng);
    console.log('  - userLocation valid:', userLocation && userLocation.lat && userLocation.lng);
    
    // Determine which location data to use (priority: result > mapMarker > searchMarker > userLocation)
    let currentLocation = null;
    
    if (result && result.lat && result.lng) {
      currentLocation = result;
      console.log('🎯 Using result as current location:', result);
    } else if (mapMarker && mapMarker.lat && mapMarker.lng) {
      currentLocation = mapMarker;
      console.log('🎯 Using mapMarker as current location:', mapMarker);
    } else if (searchMarker && searchMarker.lat && searchMarker.lng) {
      currentLocation = searchMarker;
      console.log('🎯 Using searchMarker as current location:', searchMarker);
    } else if (userLocation && userLocation.lat && userLocation.lng) {
      currentLocation = userLocation;
      console.log('🎯 Using userLocation as current location:', userLocation);
    } else {
      console.log('🎯 No current location found, trying map center fallback');
      console.log('🎯 mapInstanceRef.current:', mapInstanceRef.current);
      console.log('🎯 mapInstanceRef type:', typeof mapInstanceRef.current);
      console.log('🎯 mapInstanceRef.getCenter:', mapInstanceRef.current ? typeof mapInstanceRef.current.getCenter : 'mapInstanceRef is null');
      // Fallback: Use map center since we know the map is centered on user location
      if (mapInstanceRef.current && mapInstanceRef.current.getCenter) {
        const mapCenter = mapInstanceRef.current.getCenter();
        if (mapCenter && mapCenter.lat && mapCenter.lng) {
          console.log('🎯 Using map center as fallback location:', mapCenter);
          currentLocation = {
            lat: mapCenter.lat,
            lng: mapCenter.lng,
            address: 'Current Map Location',
            digipin: 'N/A'
          };
        } else {
          console.log('🎯 Map center not available, setting error');
          setRouteError('Please get a location first by searching, clicking on the map, or allowing location access.');
          return;
        }
      } else {
        console.log('🎯 Map not available, setting error');
        setRouteError('Please get a location first by searching, clicking on the map, or allowing location access.');
        return;
      }
    }
    
    console.log('🎯 Using location data for nearest location calculation:', {
      source: result ? 'map_click' : mapMarker ? 'map_click' : searchMarker ? 'search' : 'user_location',
      currentLocation: {
        lat: currentLocation.lat,
        lng: currentLocation.lng,
        address: currentLocation.address,
        digipin: currentLocation.digipin
      }
    });
    console.log('🎯 Current location coordinates:', currentLocation.lat, currentLocation.lng);

    setDistances([]);
    setNewAddressCoords(null);
    clearRoute();

    try {
      // Use addresses from the context
      console.log('🎯 Available addresses:', addresses.length);
      if (addresses.length === 0) {
        console.log('🎯 No addresses found');
        setRouteError('No predefined addresses found. Please add some in your profile.');
        return;
      }
      
      // First calculate aerial distances using mathematical formula to find the nearest locations
      const calculatedDistances = addresses.map(addr => {
        const aerialDistance = calculateHaversineDistance(
          currentLocation.lat, currentLocation.lng,
          parseFloat(addr.lat), parseFloat(addr.lng)
        );
        return {
          address: addr.address,
          label: addr.label,
          aerial_distance_km: aerialDistance,
          lat: addr.lat,
          lng: addr.lng,
          digipin: addr.digipin
        };
      });

      // Sort by aerial distance to get the nearest locations
      calculatedDistances.sort((a, b) => a.aerial_distance_km - b.aerial_distance_km);
      
      // Get top 3 locations with shortest aerial distances
      const top3Locations = calculatedDistances.slice(0, Math.min(3, calculatedDistances.length));
      
      // Calculate road distance for all top 3 locations
      const location1 = `${currentLocation.lat},${currentLocation.lng}`; // Current location coordinates
      const roadDistancePromises = top3Locations.map(location => 
        authenticatedFetch('/distance-between', {
          method: 'POST',
          body: JSON.stringify({ 
            location1: location1, 
            location2: location.address 
          }),
        }).then(async response => {
          if (response.ok) {
            const data = await response.json();
            return {
              ...location,
              road_distance_km: data.road_distance_km,
              road_duration: data.duration
            };
          } else {
            return {
              ...location,
              road_distance_km: null,
              road_duration: null
            };
          }
        }).catch(() => ({
          ...location,
          road_distance_km: null,
          road_duration: null
        }))
      );
      
      const locationsWithRoadDistances = await Promise.all(roadDistancePromises);
      
      // Find the location with shortest road distance
      const validLocations = locationsWithRoadDistances.filter(loc => loc.road_distance_km !== null);
      let nearestLocation = null;
      
      if (validLocations.length > 0) {
        // Sort by road distance and pick the shortest
        validLocations.sort((a, b) => a.road_distance_km - b.road_distance_km);
        nearestLocation = validLocations[0];
      } else {
        // Fallback to first location by aerial distance if no road distances available
        nearestLocation = top3Locations[0];
        nearestLocation.road_distance_km = null;
        nearestLocation.road_duration = null;
      }
      
      // Create the final result with both aerial and road distances
      const finalResult = {
        address: nearestLocation.address,
        label: nearestLocation.label,
        aerial_distance_km: nearestLocation.aerial_distance_km,
        road_distance_km: nearestLocation.road_distance_km,
        road_duration: nearestLocation.road_duration,
        lat: nearestLocation.lat,
        lng: nearestLocation.lng,
        digipin: nearestLocation.digipin
      };
      
      // Set only the nearest location result
      setDistances([finalResult]);
        
        // Update the "Search my location" input to show the nearest location
        setSpecificLocationSearch(nearestLocation.label || nearestLocation.address);
        
        // Set the current location coordinates
        setNewAddressCoords({
          lat: currentLocation.lat,
          lng: currentLocation.lng,
          address: currentLocation.address,
          digipin: currentLocation.digipin
        });

        // Calculate route to the nearest address
        const origin = `${currentLocation.lat},${currentLocation.lng}`;
        const destination = `${nearestLocation.lat},${nearestLocation.lng}`;
        
        const routeAddresses = {
          origin: {
            address: currentLocation.address,
            lat: currentLocation.lat,
            lng: currentLocation.lng,
            digipin: currentLocation.digipin
          },
          destination: {
            address: nearestLocation.address,
            lat: nearestLocation.lat,
            lng: nearestLocation.lng,
            digipin: nearestLocation.digipin
          }
        };
        
        console.log('🎯 Route addresses object created:', routeAddresses);
        console.log('🎯 Origin coordinates:', routeAddresses.origin.lat, routeAddresses.origin.lng);
        console.log('🎯 Destination coordinates:', routeAddresses.destination.lat, routeAddresses.destination.lng);
        
        // Add both origin and destination markers to mapMarkers so they're always visible
        setMapMarkers([
          // Origin marker (searched location)
          {
            lat: parseFloat(currentLocation.lat),
            lng: parseFloat(currentLocation.lng),
            address: currentLocation.address || 'Current Location',
            label: currentLocation.address || 'Current Location',
            isRouteMarker: true,
            digipin: currentLocation.digipin
          },
          // Destination marker (nearest location)
          { 
            lat: parseFloat(nearestLocation.lat), 
            lng: parseFloat(nearestLocation.lng),
            address: nearestLocation.address || nearestLocation.label,
            label: nearestLocation.label || nearestLocation.address,
            isRouteMarker: true,
            digipin: nearestLocation.digipin
          }
        ]);
        
        await calculateRoute(
          `${routeAddresses.origin.lat},${routeAddresses.origin.lng}`,
          `${routeAddresses.destination.lat},${routeAddresses.destination.lng}`,
          routeAddresses
        );
    } catch (error) {
      setRouteError('Error calculating distances. Please try again.');
      throw error; // Re-throw so handleNearestLocation can catch it
    }
  };

  // Handle nearest location calculation
  const handleNearestLocation = async () => {
    console.log('🎯 handleNearestLocation called!');
    // Set loading state for the button
    setRouteLoading(true);
    setRouteCalculationType('nearest');
    setRouteError('');
    
    console.log('🎯 Setting routeLoading to true');
    
    // Clear existing search and route markers before calculating nearest location
    clearSearchAndRouteMarkers();
    
    // Clear existing routes and route-related markers when calculating nearest location
    clearRoute();
    setMapMarkers([]); // This clears route destination markers
    setDistances([]);
    setNewAddressCoords(null);
    
    // Clear specific location dropdown but keep the field visible
    setSpecificLocationDropdownOpen(false);
    
    // Note: We do NOT clear mapMarker or searchMarker as they represent the selected location
    
    try {
      console.log('🎯 About to call calculateNearestLocationMathematical');
      // Use mathematical formula approach (faster, still provides accurate results)
      await calculateNearestLocationMathematical();
      console.log('🎯 calculateNearestLocationMathematical completed');
    } catch (error) {
      console.error('🎯 Error in handleNearestLocation:', error);
      setRouteError('Error calculating nearest location. Please try again.');
    } finally {
      console.log('🎯 Setting routeLoading to false');
      setRouteLoading(false);
    }
  };


  // Handle My Location button click - set current location to user's location
  const handleMyLocationClick = (markerData = null) => {
    console.log('🏠🏠🏠 FUNCTION CALLED - My Location button clicked 🏠🏠🏠');
    console.log('🏠 Marker data passed:', markerData);
    
    // Get current state values directly from the component state
    const currentUserLocation = userLocation;
    const currentLocationLoading = locationLoading;
    const currentLocationSource = locationSource;
    const currentResult = result;
    const currentMapMarker = mapMarker;
    const currentSearchMarker = searchMarker;
    const currentSpecificLocationSearch = specificLocationSearch;
    const currentAddresses = addresses;
    
    console.log('🏠 userLocation:', currentUserLocation);
    console.log('🏠 locationLoading:', currentLocationLoading);
    console.log('🏠 locationSource:', currentLocationSource);
    
    // Show current state of all location-related variables
    console.log('🏠 === CURRENT STATE ===');
    console.log('🏠 result:', currentResult);
    console.log('🏠 mapMarker:', currentMapMarker);
    console.log('🏠 searchMarker:', currentSearchMarker);
    console.log('🏠 userLocation:', currentUserLocation);
    console.log('🏠 specificLocationSearch:', currentSpecificLocationSearch);
    console.log('🏠 addresses count:', currentAddresses.length);
    console.log('🏠 addresses:', currentAddresses);
    console.log('🏠 === END CURRENT STATE ===');
    
    // Determine the current location to use
    let currentLocationData = null;
    
    // Priority 1: Use the marker data passed from the button click (highest priority)
    if (markerData && markerData.lat && markerData.lng) {
      console.log('🏠 My Location clicked, using marker data from button:', markerData);
      currentLocationData = {
        lat: markerData.lat,
        lng: markerData.lng,
        address: markerData.address || 'Marker Location',
        digipin: markerData.digipin || 'N/A'
      };
    }
    // Priority 2: Use selected location on map (mapMarker) if available
    else if (currentMapMarker && currentMapMarker.lat && currentMapMarker.lng) {
      console.log('🏠 My Location clicked, using selected map location:', currentMapMarker);
      currentLocationData = {
        lat: currentMapMarker.lat,
        lng: currentMapMarker.lng,
        address: currentMapMarker.address || 'Selected Location',
        digipin: currentMapMarker.digipin || 'N/A'
      };
    }
    // Priority 3: Use user's actual location if available
    else if (currentUserLocation && currentUserLocation.lat && currentUserLocation.lng) {
      console.log('🏠 My Location clicked, using user location:', currentUserLocation);
      currentLocationData = {
        lat: currentUserLocation.lat,
        lng: currentUserLocation.lng,
        address: currentUserLocation.address || 'My Location',
        digipin: currentUserLocation.digipin || 'N/A'
      };
    }
    // Priority 4: Use search result location
    else if (currentResult && currentResult.lat && currentResult.lng) {
      console.log('🏠 My Location clicked, using search result location:', currentResult);
      currentLocationData = {
        lat: currentResult.lat,
        lng: currentResult.lng,
        address: currentResult.address || 'Search Result',
        digipin: currentResult.digipin || 'N/A'
      };
    }
    // Priority 5: Use search marker location
    else if (currentSearchMarker && currentSearchMarker.lat && currentSearchMarker.lng) {
      console.log('🏠 My Location clicked, using search marker location:', currentSearchMarker);
      currentLocationData = {
        lat: currentSearchMarker.lat,
        lng: currentSearchMarker.lng,
        address: currentSearchMarker.address || 'Search Marker',
        digipin: currentSearchMarker.digipin || 'N/A'
      };
    }
    
    if (currentLocationData) {
      console.log('🏠 Using current location data:', currentLocationData);
      
      // Set the current location data
      setResult(currentLocationData);
      setSearchMarker(currentLocationData);
      
      // Clear existing search and route markers before setting new location
      clearSearchAndRouteMarkers();
      
      // Clear any existing routes
      clearRoute();
      
      console.log('🏠 Current location set');
      
      // If there's a location selected in the dropdown, automatically calculate route to it
      console.log('🏠 Checking for dropdown selection...');
      console.log('🏠 specificLocationSearch:', currentSpecificLocationSearch);
      console.log('🏠 addresses:', currentAddresses);
      
      if (currentSpecificLocationSearch && currentSpecificLocationSearch.trim() !== '') {
        console.log('🏠 Auto-calculating route to selected location:', currentSpecificLocationSearch);
        // Use the same logic as handleSpecificLocationClick
        const searchTerm = currentSpecificLocationSearch.toLowerCase().trim();
        console.log('🏠 Search term:', searchTerm);
        
        const matchingAddress = currentAddresses.find(addr => {
          const addrLabel = (addr.label || '').toLowerCase();
          const addrAddress = (addr.address || '').toLowerCase();
          
          console.log('🏠 Checking address:', { addrLabel, addrAddress, searchTerm });
          
          return addrLabel === searchTerm || 
                 addrAddress === searchTerm ||
                 addrLabel.includes(searchTerm) ||
                 addrAddress.includes(searchTerm);
        });
        
        console.log('🏠 Matching address found:', matchingAddress);
        
        if (matchingAddress) {
          console.log('🏠 Found matching address, calculating route:', matchingAddress);
          console.log('🏠 About to call handleSpecificLocationDistanceCalculation with:');
          console.log('🏠   - selectedAddress:', matchingAddress);
          console.log('🏠   - currentLocation:', currentLocationData);
          setRouteCalculationType('specific');
          // Call the function directly - it will be defined later in the component
          setTimeout(() => {
            if (window.handleSpecificLocationDistanceCalculation) {
              window.handleSpecificLocationDistanceCalculation(matchingAddress, currentLocationData);
            }
          }, 0);
        } else {
          console.log('🏠 No matching address found');
          console.log('🏠 Available addresses for comparison:');
          currentAddresses.forEach((addr, index) => {
            console.log(`🏠   ${index}: ${addr.label || addr.address} (${addr.address})`);
          });
        }
      } else {
        console.log('🏠 No specificLocationSearch or empty string');
          // Show helpful message instead of automatically using first address
          if (currentAddresses.length > 0) {
            console.log('🏠 No specific location selected, showing helpful message');
            showToastMessage('Please select a location from the "My Locations" dropdown first, or set a default location in your settings.');
          } else {
            console.log('🏠 No addresses available');
            showToastMessage('You have no saved locations. Please add locations to "My Locations" first.');
          }
      }
    } else {
      console.log('🏠 No user location available');
      console.log('🏠 userLocation type:', typeof currentUserLocation);
      console.log('🏠 userLocation value:', currentUserLocation);
      
      // Fallback: Use map center if available
      if (mapInstanceRef.current && mapInstanceRef.current.getCenter) {
        const mapCenter = mapInstanceRef.current.getCenter();
        if (mapCenter && mapCenter.lat && mapCenter.lng) {
          console.log('🏠 Using map center as fallback:', mapCenter);
          
          currentLocationData = {
            lat: mapCenter.lat,
            lng: mapCenter.lng,
            address: 'Map Center Location',
            digipin: 'N/A'
          };
          
          // Set both result and searchMarker to ensure it's used for route calculation
          setResult(currentLocationData);
          setSearchMarker(currentLocationData);
          
          // Clear existing search and route markers before setting new location
          clearSearchAndRouteMarkers();
          
          // Clear any existing routes
          clearRoute();
          
          console.log('🏠 Map center set as current location');
          
          // If there's a location selected in the dropdown, automatically calculate route to it
          if (currentSpecificLocationSearch && currentSpecificLocationSearch.trim() !== '') {
            console.log('🏠 Auto-calculating route to selected location (fallback):', currentSpecificLocationSearch);
            // Use the same logic as handleSpecificLocationClick
            const searchTerm = currentSpecificLocationSearch.toLowerCase().trim();
            const matchingAddress = currentAddresses.find(addr => {
              const addrLabel = (addr.label || '').toLowerCase();
              const addrAddress = (addr.address || '').toLowerCase();
              
              return addrLabel === searchTerm || 
                     addrAddress === searchTerm ||
                     addrLabel.includes(searchTerm) ||
                     addrAddress.includes(searchTerm);
            });
            
            if (matchingAddress) {
              console.log('🏠 Found matching address (fallback), calculating route:', matchingAddress);
              console.log('🏠 About to call handleSpecificLocationDistanceCalculation (fallback) with:');
              console.log('🏠   - selectedAddress:', matchingAddress);
              console.log('🏠   - currentLocation:', currentLocationData);
              setRouteCalculationType('specific');
              // Call the function directly - it will be defined later in the component
              setTimeout(() => {
                if (window.handleSpecificLocationDistanceCalculation) {
                  window.handleSpecificLocationDistanceCalculation(matchingAddress, currentLocationData);
                }
              }, 0);
            } else {
              console.log('🏠 No matching address found (fallback)');
              console.log('🏠 Available addresses for comparison (fallback):');
              currentAddresses.forEach((addr, index) => {
                console.log(`🏠   ${index}: ${addr.label || addr.address} (${addr.address})`);
              });
            }
          } else {
            console.log('🏠 No specificLocationSearch (fallback), using first address as default');
            // Use first address as default if no specific selection
            if (currentAddresses.length > 0) {
              console.log('🏠 Using first address as default (fallback):', currentAddresses[0]);
              setRouteCalculationType('specific');
              // Call the function directly - it will be defined later in the component
              setTimeout(() => {
                if (window.handleSpecificLocationDistanceCalculation) {
                  window.handleSpecificLocationDistanceCalculation(currentAddresses[0], currentLocationData);
                }
              }, 0);
            } else {
              console.log('🏠 No addresses available for route calculation (fallback)');
            }
          }
        } else {
          console.log('🏠 Map center not available either');
        }
      } else {
        console.log('🏠 Map instance not available');
      }
    }
  };

  // Handle specific location button click (for dropdown functionality)
  const handleSpecificLocationClick = () => {
    // Check if there's already a populated location in the search field
    if (specificLocationSearch && specificLocationSearch.trim() !== '') {
      // Find the matching address from the addresses list (case-insensitive)
      const searchTerm = specificLocationSearch.toLowerCase().trim();
      const matchingAddress = addresses.find(addr => {
        const addrLabel = (addr.label || '').toLowerCase();
        const addrAddress = (addr.address || '').toLowerCase();
        
        return addrLabel === searchTerm || 
               addrAddress === searchTerm ||
               addrLabel.includes(searchTerm) ||
               addrAddress.includes(searchTerm);
      });
      
      if (matchingAddress) {
        // If we found a matching address, calculate route to it
        console.log('Using pre-populated location:', matchingAddress);
        
        // Determine which location data to use (priority: result > mapMarker > searchMarker > userLocation)
        let currentLocation = null;
        
        if (result && result.lat && result.lng) {
          currentLocation = result;
        } else if (mapMarker && mapMarker.lat && mapMarker.lng) {
          currentLocation = mapMarker;
        } else if (searchMarker && searchMarker.lat && searchMarker.lng) {
          currentLocation = searchMarker;
        } else if (userLocation && userLocation.lat && userLocation.lng) {
          currentLocation = userLocation;
        }
        
        // Calculate distance between current location and selected specific location
        if (currentLocation) {
          console.log('Calling handleSpecificLocationDistanceCalculation with current location:', currentLocation);
          setRouteCalculationType('specific');
          handleSpecificLocationDistanceCalculation(matchingAddress, currentLocation);
        } else {
          console.log('No current location available for distance calculation');
          setRouteError('Please select a location first by clicking on the map or searching.');
        }
        return;
      }
    }
    
    // If no populated location or no match found, show the dropdown for selection
    setShowSpecificLocationField(true);
    setSpecificLocationDropdownOpen(true);
    setHighlightedSpecificIndex(-1);
  };

  // Handle specific location distance calculation - using same pattern as nearest location
  const handleSpecificLocationDistanceCalculation = async (selectedAddress, currentLocation) => {
    console.log('handleSpecificLocationDistanceCalculation called with:', { selectedAddress, currentLocation });
    
    if (!currentLocation || !currentLocation.lat || !currentLocation.lng) {
      setRouteError('Please get a location first by searching, clicking on the map, or allowing location access.');
      return;
    }
    
    console.log('Using location data for specific location calculation:', {
      source: result ? 'map_click' : mapMarker ? 'map_click' : searchMarker ? 'search' : 'user_location',
      currentLocation: {
        lat: currentLocation.lat,
        lng: currentLocation.lng,
        address: currentLocation.address
      },
      selectedAddress: selectedAddress
    });

    setRouteLoading(true);
    setRouteError('');
    setDistances([]);
    setNewAddressCoords(null);
    
    // Clear existing search and route markers before calculating specific location
    clearSearchAndRouteMarkers();
    
    clearRoute();

    try {
      // Use the efficient distance-between API to get road distance for the selected location
      const location1 = `${currentLocation.lat},${currentLocation.lng}`; // Current location coordinates
      const location2 = selectedAddress.address; // Selected location address
      
      const response = await authenticatedFetch('/distance-between', {
        method: 'POST',
        body: JSON.stringify({ 
          location1: location1, 
          location2: location2 
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Calculate aerial distance using mathematical formula
        const aerialDistance = calculateHaversineDistance(
          currentLocation.lat, currentLocation.lng,
          parseFloat(selectedAddress.lat), parseFloat(selectedAddress.lng)
        );
        
        // Create the final result with both aerial and road distances (same format as nearest location)
        const finalResult = {
          address: selectedAddress.address,
          label: selectedAddress.label,
          aerial_distance_km: aerialDistance,
          road_distance_km: data.road_distance_km,
          road_duration: data.duration,
          lat: selectedAddress.lat,
          lng: selectedAddress.lng,
          digipin: selectedAddress.digipin
        };
        
        // Set the result in distances array (same as nearest location)
        setDistances([finalResult]);
        
        // Set the current location coordinates (same as nearest location)
        setNewAddressCoords({
          lat: currentLocation.lat,
          lng: currentLocation.lng,
          address: currentLocation.address,
          digipin: currentLocation.digipin
        });

        // Calculate route to the selected address (same as nearest location)
        const origin = `${currentLocation.lat},${currentLocation.lng}`;
        const destination = `${selectedAddress.lat},${selectedAddress.lng}`;
        
        const routeAddresses = {
          origin: {
            address: currentLocation.address,
            lat: currentLocation.lat,
            lng: currentLocation.lng,
            digipin: currentLocation.digipin
          },
          destination: {
            address: selectedAddress.address,
            lat: selectedAddress.lat,
            lng: selectedAddress.lng,
            digipin: selectedAddress.digipin
          }
        };
        
        // Add both origin and destination markers to mapMarkers so they're always visible
        setMapMarkers([
          // Origin marker (searched location)
          {
            lat: parseFloat(currentLocation.lat),
            lng: parseFloat(currentLocation.lng),
            address: currentLocation.address || 'Current Location',
            label: currentLocation.address || 'Current Location',
            isRouteMarker: true,
            digipin: currentLocation.digipin
          },
          // Destination marker (selected location)
          { 
            lat: parseFloat(selectedAddress.lat), 
            lng: parseFloat(selectedAddress.lng),
            address: selectedAddress.address || selectedAddress.label,
            label: selectedAddress.label || selectedAddress.address,
            isRouteMarker: true,
            digipin: selectedAddress.digipin
          }
        ]);
        
        await calculateRoute(
          `${routeAddresses.origin.lat},${routeAddresses.origin.lng}`,
          `${routeAddresses.destination.lat},${routeAddresses.destination.lng}`,
          routeAddresses
        );
      } else {
        // If API call fails, show only the selected location with aerial distance (same as nearest location)
        const aerialDistance = calculateHaversineDistance(
          currentLocation.lat, currentLocation.lng,
          parseFloat(selectedAddress.lat), parseFloat(selectedAddress.lng)
        );
        
        const aerialOnlyResult = {
          address: selectedAddress.address,
          label: selectedAddress.label,
          aerial_distance_km: aerialDistance,
          road_distance_km: null,
          road_duration: null,
          lat: selectedAddress.lat,
          lng: selectedAddress.lng,
          digipin: selectedAddress.digipin
        };
        
        setDistances([aerialOnlyResult]);
        
        // Set the selected location coordinates
        setNewAddressCoords({
          lat: parseFloat(selectedAddress.lat),
          lng: parseFloat(selectedAddress.lng),
          digipin: selectedAddress.digipin
        });

        // Create route addresses object for map display
        const routeAddresses = {
          origin: {
            address: currentLocation.address,
            lat: currentLocation.lat,
            lng: currentLocation.lng,
            digipin: currentLocation.digipin
          },
          destination: {
            address: selectedAddress.address,
            lat: selectedAddress.lat,
            lng: selectedAddress.lng,
            digipin: selectedAddress.digipin
          }
        };

        // Add both origin and destination markers to mapMarkers so they're always visible
        setMapMarkers([
          // Origin marker (searched location)
          {
            lat: parseFloat(currentLocation.lat),
            lng: parseFloat(currentLocation.lng),
            address: currentLocation.address || 'Current Location',
            label: currentLocation.address || 'Current Location',
            isRouteMarker: true,
            digipin: currentLocation.digipin
          },
          // Destination marker (selected location)
          { 
            lat: parseFloat(selectedAddress.lat), 
            lng: parseFloat(selectedAddress.lng),
            address: selectedAddress.address || selectedAddress.label,
            label: selectedAddress.label || selectedAddress.address,
            isRouteMarker: true,
            digipin: selectedAddress.digipin
          }
        ]);

        await calculateRoute(
          `${routeAddresses.origin.lat},${routeAddresses.origin.lng}`,
          `${routeAddresses.destination.lat},${routeAddresses.destination.lng}`,
          routeAddresses
        );
      }
    } catch (error) {
      setRouteError('Error calculating distances. Please try again.');
      throw error;
    } finally {
      setRouteLoading(false);
    }
  };

  // Handle PDF generation for single location
  const handleGeneratePDF = async (locationInfo) => {
    console.log('📄 PDF generation requested for:', locationInfo);
    
    try {
      // Try to get the best available address from current state
      let bestAddress = locationInfo.address;
      
      // If the passed address is generic, try to get better address from current state
      const genericTerms = ['Search Result', 'Selected Location', 'Search Marker', 'Marker Location'];
      if (!bestAddress || genericTerms.includes(bestAddress)) {
        console.log('📄 Address is generic, trying to get better address from state...');
        
        // Try to get address from result, mapMarker, or searchMarker state
        if (result && result.address && !genericTerms.includes(result.address)) {
          bestAddress = result.address;
          console.log('📄 Using result.address:', bestAddress);
        } else if (mapMarker && mapMarker.address && !genericTerms.includes(mapMarker.address)) {
          bestAddress = mapMarker.address;
          console.log('📄 Using mapMarker.address:', bestAddress);
        } else if (searchMarker && searchMarker.address && !genericTerms.includes(searchMarker.address)) {
          bestAddress = searchMarker.address;
          console.log('📄 Using searchMarker.address:', bestAddress);
        } else if (input && input.trim() !== '') {
          bestAddress = input.trim();
          console.log('📄 Using input field value:', bestAddress);
        }
      }
      
      // Get current map zoom level
      let currentZoom = 16; // Default fallback
      if (mapInstanceRef.current && mapInstanceRef.current.getZoom) {
        currentZoom = mapInstanceRef.current.getZoom();
        console.log('📏 Current map zoom level from interactive map:', currentZoom);
      } else {
        console.log('📏 Map instance not available, using default zoom:', currentZoom);
        console.log('📏 mapInstanceRef.current:', mapInstanceRef.current);
      }

      // Create the data structure for single point PDF generation
      const singlePointData = {
        address: bestAddress || locationInfo.address || 'Selected Location',
        digipin: locationInfo.digipin || (result && result.digipin) || (mapMarker && mapMarker.digipin) || (searchMarker && searchMarker.digipin) || 'N/A',
        lat: locationInfo.lat,
        lng: locationInfo.lng,
        search_input: bestAddress || input || locationInfo.address || `${locationInfo.lat},${locationInfo.lng}`,
        source: 'pin2point_leaflet',
        currentZoom: currentZoom // Pass current zoom level
      };
      
      console.log('📄 Final PDF data structure:', singlePointData);

      // Generate PDF using the unified PDF generator
      await generateUnifiedPDF(singlePointData, {
        type: 'single',
        userEmail: userEmail
      });
      
      console.log('✅ PDF generated successfully');
    } catch (error) {
      console.error('❌ Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires');
    window.location.href = '/';
  };

  // Click outside handlers for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('[data-dropdown]')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Fetch input history
  const fetchInputHistory = async () => {
    try {
      const response = await authenticatedFetch('/usage-history');
      if (response.ok) {
        const data = await response.json();
        setUsageHistory(data);
        
        const inputRecords = data
          .filter(record => (record.address && record.address.trim() !== '') || 
                           (record.digipin && record.digipin.trim() !== ''))
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        const uniqueInputs = [];
        const seenInputs = new Set();
        
        for (const record of inputRecords) {
          let searchInput = '';
          let searchType = '';
          
          if (record.latitude && record.longitude && record.source === 'combined_digipin') {
            searchInput = `${record.latitude},${record.longitude}`;
            searchType = 'latlong';
          }
          else if (record.address && record.action === 'reverse') {
            searchInput = record.address;
            searchType = 'address';
          }
          else if (record.digipin && record.action === 'lookup') {
            searchInput = record.digipin;
            searchType = 'digipin';
          }
          else if (record.address) {
            searchInput = record.address;
            searchType = 'address';
          } else if (record.digipin) {
            searchInput = record.digipin;
            searchType = 'digipin';
          }
          
          if (searchInput && !seenInputs.has(`${searchType}:${searchInput}`)) {
            uniqueInputs.push({ value: searchInput, type: searchType });
            seenInputs.add(`${searchType}:${searchInput}`);
          }
          
          if (uniqueInputs.length >= 10) break;
        }
        
        setInputHistory(uniqueInputs);
      }
    } catch (error) {
      // Error handling
    }
  };

  // Filter recent searches based on input
  const filteredRecentSearches = inputHistory.filter(item => 
    item.value.toLowerCase().includes(recentSearchTerm.toLowerCase())
  ).slice(0, 10);

  // Handle recent search selection
  const selectRecentSearch = (historyInput) => {
    setInput(historyInput.value);
    setRecentSearchTerm("");
    setShowRecentSearchesDropdown(false);
    setHighlightedRecentIndex(-1);
    
    // Find the most recent record for this input
    const record = usageHistory
      .filter(r => (historyInput.type === 'address' && r.address === historyInput.value) ||
                   (historyInput.type === 'digipin' && r.digipin === historyInput.value))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
    
    if (record) {
      const safeAddress = typeof record.address === 'string' ? record.address : '';
      const safeDigipin = typeof record.digipin === 'string' ? record.digipin : '';
      const safeLatitude = typeof record.latitude === 'string' ? parseFloat(record.latitude) : 0;
      const safeLongitude = typeof record.longitude === 'string' ? parseFloat(record.longitude) : 0;
      
      setResult({
        address: safeAddress,
        digipin: safeDigipin,
        lat: safeLatitude,
        lng: safeLongitude
      });
      setLatitude(safeLatitude);
      setLongitude(safeLongitude);
      setSearchMarker({ lat: safeLatitude, lng: safeLongitude });
      setError('');
      setLoading(false);
      
      // Add marker to map
      addMarker(safeLatitude, safeLongitude, safeAddress, true, false, safeDigipin);
    } else {
      // Fallback to search
      setResult('');
      setError('');
      setLatitude('');
      setLongitude('');
      setSearchMarker(null);
      setInput(historyInput.value);
      handleSearch({ preventDefault: () => {} });
    }
  };

  // Handle main search input key events
  const handleMainSearchKeyDown = (e) => {
    if (showRecentSearchesDropdown && filteredRecentSearches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedRecentIndex((prev) => (prev + 1) % filteredRecentSearches.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedRecentIndex((prev) => (prev - 1 + filteredRecentSearches.length) % filteredRecentSearches.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedRecentIndex >= 0 && highlightedRecentIndex < filteredRecentSearches.length) {
          selectRecentSearch(filteredRecentSearches[highlightedRecentIndex]);
        } else {
          handleSearch(e);
        }
      } else if (e.key === 'Escape') {
        setShowRecentSearchesDropdown(false);
        setHighlightedRecentIndex(-1);
      }
    } else if (e.key === 'Enter') {
      handleSearch(e);
    }
  };

  // Handle click outside to close recent searches dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        recentDropdownRef.current &&
        !recentDropdownRef.current.contains(event.target) &&
        mainSearchRef.current &&
        !mainSearchRef.current.contains(event.target)
      ) {
        setShowRecentSearchesDropdown(false);
        setHighlightedRecentIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Location management handler functions
  const resetForm = () => {
    setFormData({
      input: '',
      label: '',
      inputType: 'address',
      place_id: null,
      setAsDefault: false
    });
    setShowAddModal(false);
    setFormMessage('');
  };

  const handleAddToMyLocation = (locationInfo) => {
    const shouldSetAsDefault = addresses.length === 0;
    
    setFormData({
      input: locationInfo.address,
      label: locationInfo.label || locationInfo.address,
      inputType: 'address',
      place_id: locationInfo.place_id || null,
      setAsDefault: shouldSetAsDefault
    });
    setShowAddModal(true);
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    if (!formData.input.trim() || !formData.label.trim()) {
      setFormMessage('Please provide both input and label.');
      return;
    }

    setFormLoading(true);
    setFormMessage('');

    try {
      const response = await authenticatedFetch('/addresses', {
        method: 'POST',
        body: JSON.stringify({
          address: formData.input,
          label: formData.label,
          input_type: formData.inputType,
          place_id: formData.place_id
        }),
      });

      if (response.ok) {
        const addedLocation = await response.json();
        
        if (formData.setAsDefault) {
          const defaultResponse = await authenticatedFetch('/addresses/set-default', {
            method: 'POST',
            body: JSON.stringify({
              address_id: addedLocation.id
            }),
          });
          
          if (defaultResponse.ok) {
            setFormMessage('Location added and set as default location successfully!');
          } else {
            setFormMessage('Location added successfully, but failed to set as default location.');
          }
        } else {
          setFormMessage('Location added successfully!');
        }
        
        resetForm();
        await contextRefreshAddresses();
      } else {
        const errorData = await response.json();
        setFormMessage(`Error: ${errorData.detail || errorData.message || 'Failed to add location.'}`);
      }
    } catch (error) {
      setFormMessage(`Error: ${error.message || 'Could not save location. Please try again.'}`);
    } finally {
      setFormLoading(false);
    }
  };

  // Expose functions to global window for popup buttons
  useEffect(() => {
    console.log('🔧 Exposing functions to global window...');
    console.log('🔧 handleNearestLocation:', typeof handleNearestLocation);
    console.log('🔧 handleMyLocationClick:', typeof handleMyLocationClick);
    
    window.addToMyLocation = handleAddToMyLocation;
    window.handleNearestLocation = handleNearestLocation;
    window.handleGeneratePDF = handleGeneratePDF;
    window.handleSpecificLocationClick = handleSpecificLocationClick;
    
    // Create route PDF generation function
    window.generateRouteMultiLocationPDF = async () => {
      console.log('📄 Route PDF generation requested from route info marker');
      
      // Check if we have route data available
      const currentDistances = distances;
      const currentResult = result;
      const currentNewAddressCoords = newAddressCoords;
      const currentRoutePath = routePath;
      
      // Note: For route PDFs, zoom level will be auto-calculated to fit the entire route
      console.log('📏 Route PDF - Will use auto-calculated zoom to fit entire route');
      
      if (currentDistances && currentDistances.length > 0) {
        const routeData = {
          fromData: {
            address: currentNewAddressCoords?.address || (currentResult?.address) || 'Current Location',
            digipin: currentNewAddressCoords?.digipin || (currentResult?.digipin) || 'N/A',
            lat: currentNewAddressCoords?.lat || (currentResult?.lat),
            lng: currentNewAddressCoords?.lng || (currentResult?.lng)
          },
          toData: {
            address: currentDistances[0]?.address || currentDistances[0]?.label || 'Destination',
            label: currentDistances[0]?.label || currentDistances[0]?.address || 'Destination',
            digipin: currentDistances[0]?.digipin || 'N/A',
            lat: currentDistances[0]?.lat,
            lng: currentDistances[0]?.lng
          },
          distanceData: {
            road_distance_km: currentDistances[0]?.road_distance_km,
            road_duration: currentDistances[0]?.road_duration,
            aerial_distance_km: currentDistances[0]?.aerial_distance_km
          },
          routePath: currentRoutePath, // Include actual route path coordinates
          source: 'pin2point_route'
          // Note: currentZoom removed - will be auto-calculated to fit route
        };
        
        console.log('📄 Generating route PDF with data:', routeData);
        console.log('📄 Route path available:', !!currentRoutePath, 'points:', currentRoutePath?.length || 0);
        
        // Call the route PDF generation function with correct format
        try {
          await generateUnifiedPDF(routeData, {
            type: 'route',
            userEmail: userEmail
          });
          console.log('✅ Route PDF generated successfully');
        } catch (error) {
          console.error('❌ Error generating route PDF:', error);
          alert('Error generating route PDF. Please try again.');
        }
      } else {
        console.warn('⚠️ No route data available for PDF generation');
        alert('No route data available for PDF generation');
      }
    };
    
    // Create a wrapper function that captures current state values
    window.handleMyLocationClick = (markerData = null) => {
      console.log('🏠🏠🏠 GLOBAL WRAPPER CALLED - My Location button clicked 🏠🏠🏠');
      console.log('🏠 Marker data passed to wrapper:', markerData);
      
      // Get current state values directly from the component state
      const currentUserLocation = userLocation;
      const currentLocationLoading = locationLoading;
      const currentLocationSource = locationSource;
      const currentResult = result;
      const currentMapMarker = mapMarker;
      const currentSearchMarker = searchMarker;
      const currentSpecificLocationSearch = specificLocationSearch;
      const currentAddresses = addresses;
      
      console.log('🏠 userLocation:', currentUserLocation);
      console.log('🏠 locationLoading:', currentLocationLoading);
      console.log('🏠 locationSource:', currentLocationSource);
      
      // Show current state of all location-related variables
      console.log('🏠 === CURRENT STATE ===');
      console.log('🏠 result:', currentResult);
      console.log('🏠 mapMarker:', currentMapMarker);
      console.log('🏠 searchMarker:', currentSearchMarker);
      console.log('🏠 userLocation:', currentUserLocation);
      console.log('🏠 specificLocationSearch:', currentSpecificLocationSearch);
      console.log('🏠 addresses count:', currentAddresses.length);
      console.log('🏠 addresses:', currentAddresses);
      console.log('🏠 === END CURRENT STATE ===');
      
      // Determine the current location to use
      let currentLocationData = null;
      
      // Priority 1: Use the marker data passed from the button click (highest priority)
      if (markerData && markerData.lat && markerData.lng) {
        console.log('🏠 My Location clicked, using marker data from button:', markerData);
        currentLocationData = {
          lat: markerData.lat,
          lng: markerData.lng,
          address: markerData.address || 'Marker Location',
          digipin: markerData.digipin || 'N/A'
        };
      }
      // Priority 2: Use selected location on map (mapMarker) if available
      else if (currentMapMarker && currentMapMarker.lat && currentMapMarker.lng) {
        console.log('🏠 My Location clicked, using selected map location:', currentMapMarker);
        currentLocationData = {
          lat: currentMapMarker.lat,
          lng: currentMapMarker.lng,
          address: currentMapMarker.address || 'Selected Location',
          digipin: currentMapMarker.digipin || 'N/A'
        };
      }
      // Priority 3: Use user's actual location if available
      else if (currentUserLocation && currentUserLocation.lat && currentUserLocation.lng) {
        console.log('🏠 My Location clicked, using user location:', currentUserLocation);
        currentLocationData = {
          lat: currentUserLocation.lat,
          lng: currentUserLocation.lng,
          address: currentUserLocation.address || 'My Location',
          digipin: currentUserLocation.digipin || 'N/A'
        };
      }
      // Priority 4: Use search result location
      else if (currentResult && currentResult.lat && currentResult.lng) {
        console.log('🏠 My Location clicked, using search result location:', currentResult);
        currentLocationData = {
          lat: currentResult.lat,
          lng: currentResult.lng,
          address: currentResult.address || 'Search Result',
          digipin: currentResult.digipin || 'N/A'
        };
      }
      // Priority 5: Use search marker location
      else if (currentSearchMarker && currentSearchMarker.lat && currentSearchMarker.lng) {
        console.log('🏠 My Location clicked, using search marker location:', currentSearchMarker);
        currentLocationData = {
          lat: currentSearchMarker.lat,
          lng: currentSearchMarker.lng,
          address: currentSearchMarker.address || 'Search Marker',
          digipin: currentSearchMarker.digipin || 'N/A'
        };
      }
      
      if (currentLocationData) {
        console.log('🏠 Using current location data:', currentLocationData);
        
        // Set the current location data
        setResult(currentLocationData);
        setSearchMarker(currentLocationData);
        
        // Clear existing search and route markers before setting new location
        clearSearchAndRouteMarkers();
        
        // Clear any existing routes
        clearRoute();
        
        console.log('🏠 Current location set');
        
        // If there's a location selected in the dropdown, automatically calculate route to it
        console.log('🏠 Checking for dropdown selection...');
        console.log('🏠 specificLocationSearch:', currentSpecificLocationSearch);
        console.log('🏠 addresses:', currentAddresses);
        
        if (currentSpecificLocationSearch && currentSpecificLocationSearch.trim() !== '') {
          console.log('🏠 Auto-calculating route to selected location:', currentSpecificLocationSearch);
          // Use the same logic as handleSpecificLocationClick
          const searchTerm = currentSpecificLocationSearch.toLowerCase().trim();
          console.log('🏠 Search term:', searchTerm);
          
          const matchingAddress = currentAddresses.find(addr => {
            const addrLabel = (addr.label || '').toLowerCase();
            const addrAddress = (addr.address || '').toLowerCase();
            
            console.log('🏠 Checking address:', { addrLabel, addrAddress, searchTerm });
            
            return addrLabel === searchTerm || 
                   addrAddress === searchTerm ||
                   addrLabel.includes(searchTerm) ||
                   addrAddress.includes(searchTerm);
          });
          
          console.log('🏠 Matching address found:', matchingAddress);
          
          if (matchingAddress) {
            console.log('🏠 Found matching address, calculating route:', matchingAddress);
            console.log('🏠 About to call handleSpecificLocationDistanceCalculation with:');
            console.log('🏠   - selectedAddress:', matchingAddress);
            console.log('🏠   - currentLocation:', currentLocationData);
            setRouteCalculationType('specific');
            // Call the function directly - it will be defined later in the component
            setTimeout(() => {
              if (window.handleSpecificLocationDistanceCalculation) {
                window.handleSpecificLocationDistanceCalculation(matchingAddress, currentLocationData);
              }
            }, 0);
          } else {
            console.log('🏠 No matching address found');
            console.log('🏠 Available addresses for comparison:');
            currentAddresses.forEach((addr, index) => {
              console.log(`🏠   ${index}: ${addr.label || addr.address} (${addr.address})`);
            });
          }
        } else {
          console.log('🏠 No specificLocationSearch or empty string');
          // Show helpful message instead of automatically using first address
          if (currentAddresses.length > 0) {
            console.log('🏠 No specific location selected, showing helpful message');
            showToastMessage('Please select a location from the "My Locations" dropdown first, or set a default location in your settings.');
          } else {
            console.log('🏠 No addresses available');
            showToastMessage('You have no saved locations. Please add locations to "My Locations" first.');
          }
        }
      } else {
        console.log('🏠 No user location available');
        console.log('🏠 userLocation type:', typeof currentUserLocation);
        console.log('🏠 userLocation value:', currentUserLocation);
        
        // Fallback: Use map center if available
        if (mapInstanceRef.current && mapInstanceRef.current.getCenter) {
          const mapCenter = mapInstanceRef.current.getCenter();
          if (mapCenter && mapCenter.lat && mapCenter.lng) {
            console.log('🏠 Using map center as fallback:', mapCenter);
            
            currentLocationData = {
              lat: mapCenter.lat,
              lng: mapCenter.lng,
              address: 'Map Center Location',
              digipin: 'N/A'
            };
            
            // Set both result and searchMarker to ensure it's used for route calculation
            setResult(currentLocationData);
            setSearchMarker(currentLocationData);
            
            // Clear existing search and route markers before setting new location
            clearSearchAndRouteMarkers();
            
            // Clear any existing routes
            clearRoute();
            
            console.log('🏠 Map center set as current location');
            
            // If there's a location selected in the dropdown, automatically calculate route to it
            if (currentSpecificLocationSearch && currentSpecificLocationSearch.trim() !== '') {
              console.log('🏠 Auto-calculating route to selected location (fallback):', currentSpecificLocationSearch);
              // Use the same logic as handleSpecificLocationClick
              const searchTerm = currentSpecificLocationSearch.toLowerCase().trim();
              const matchingAddress = currentAddresses.find(addr => {
                const addrLabel = (addr.label || '').toLowerCase();
                const addrAddress = (addr.address || '').toLowerCase();
                
                return addrLabel === searchTerm || 
                       addrAddress === searchTerm ||
                       addrLabel.includes(searchTerm) ||
                       addrAddress.includes(searchTerm);
              });
              
              if (matchingAddress) {
                console.log('🏠 Found matching address (fallback), calculating route:', matchingAddress);
                console.log('🏠 About to call handleSpecificLocationDistanceCalculation (fallback) with:');
                console.log('🏠   - selectedAddress:', matchingAddress);
                console.log('🏠   - currentLocation:', currentLocationData);
                setRouteCalculationType('specific');
                // Call the function directly - it will be defined later in the component
                setTimeout(() => {
                  if (window.handleSpecificLocationDistanceCalculation) {
                    window.handleSpecificLocationDistanceCalculation(matchingAddress, currentLocationData);
                  }
                }, 0);
              } else {
                console.log('🏠 No matching address found (fallback)');
                console.log('🏠 Available addresses for comparison (fallback):');
                currentAddresses.forEach((addr, index) => {
                  console.log(`🏠   ${index}: ${addr.label || addr.address} (${addr.address})`);
                });
              }
            } else {
              console.log('🏠 No specificLocationSearch (fallback)');
              // Show helpful message instead of automatically using first address
              if (currentAddresses.length > 0) {
                console.log('🏠 No specific location selected (fallback), showing helpful message');
                showToastMessage('Please select a location from the "My Locations" dropdown first, or set a default location in your settings.');
              } else {
                console.log('🏠 No addresses available (fallback)');
                showToastMessage('You have no saved locations. Please add locations to "My Locations" first.');
              }
            }
          } else {
            console.log('🏠 Map center not available either');
          }
        } else {
          console.log('🏠 Map instance not available');
        }
      }
    };
    
    window.handleSpecificLocationDistanceCalculation = handleSpecificLocationDistanceCalculation;
    
    console.log('🔧 window.handleNearestLocation:', typeof window.handleNearestLocation);
    console.log('🔧 window.handleMyLocationClick:', typeof window.handleMyLocationClick);
    console.log('🔧 All functions exposed successfully!');
    
    // Cleanup on unmount
    return () => {
      delete window.addToMyLocation;
      delete window.handleNearestLocation;
      delete window.handleGeneratePDF;
      delete window.handleSpecificLocationClick;
      delete window.handleMyLocationClick;
      delete window.handleSpecificLocationDistanceCalculation;
    };
  }, [userLocation, locationLoading, locationSource, result, mapMarker, searchMarker, specificLocationSearch, addresses, mapInstanceRef, setRouteCalculationType, clearRoute, setResult, setSearchMarker, handleSpecificLocationDistanceCalculation, showToastMessage]);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      {/* Full-screen Leaflet map container */}
      <div style={{ 
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1
      }}>
        {!locationLoading ? (
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <div 
              ref={mapRef}
              style={{ 
                width: '100%',
                height: '100%',
                minHeight: '400px' // Ensure minimum height
              }}
            />
            
            {(addressesLoading || loading) && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
              }}>
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #007bff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 10px'
                  }}></div>
                  <p style={{ margin: 0, color: '#333', fontSize: '16px', fontWeight: '500' }}>
                    {addressesLoading ? 'Loading your locations...' : 
                     loading ? 'Searching for location...' :
                     'Getting location details...'}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ flex: '1 1 0%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', color: '#666', fontSize: '16px' }}>
            <div style={{ textAlign: 'center' }}>
              <p>{locationLoading ? 'Detecting your location...' : 'Loading map...'}</p>
              <p style={{ fontSize: '14px', marginTop: '10px' }}>
                {locationLoading ? 'Please wait while we detect your location.' : 'Please wait while we load the map.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Floating navigation buttons - top right horizontal */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '100px',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'row',
        gap: '8px'
      }}>
        <Link to="/pin2point" style={{
          background: 'white',
          border: '1px solid #007bff',
          borderRadius: '8px',
          padding: '8px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '13px',
          fontWeight: '500',
          textDecoration: 'none',
          color: '#007bff',
          width: 'auto',
          boxSizing: 'border-box'
        }}>
          <span style={{ fontSize: '16px' }}>🗺️</span>
          Google Maps
        </Link>
        
        <Link to="/bulk-search" style={{
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '8px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '13px',
          fontWeight: '500',
          textDecoration: 'none',
          color: '#333',
          width: 'auto',
          boxSizing: 'border-box'
        }}>
          <span style={{ fontSize: '16px' }}>📊</span>
          Batch Processor
        </Link>
        
        <Link to="/usage-history" style={{
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '8px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '13px',
          fontWeight: '500',
          textDecoration: 'none',
          color: '#333',
          width: 'auto',
          boxSizing: 'border-box'
        }}>
          <span style={{ fontSize: '16px' }}>📋</span>
          History
        </Link>
      </div>

      {/* Settings button */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '50px',
        zIndex: 1000
      }} data-dropdown>
        <button
          style={{
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '50%',
            width: '42px',
            height: '42px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            fontSize: '18px'
          }}
          onClick={() => setShowDropdown(prev => !prev)}
        >
          ⚙️
        </button>
        {showDropdown && (
          <div style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: '8px',
            background: 'white',
            minWidth: '140px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: '6px',
            zIndex: 2000,
            border: '1px solid #eee'
          }}>
            <Link to="/profile" style={{ display: 'block', padding: '10px 12px', color: '#333', textDecoration: 'none', borderBottom: '1px solid #eee', fontSize: '13px' }} onClick={() => setShowDropdown(false)}>Profile</Link>
            <button 
              onClick={() => {
                handleLogout();
                setShowDropdown(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px 12px',
                background: 'none',
                border: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                color: '#dc3545',
                fontSize: '13px'
              }}
            >
              Logout
            </button>
          </div>
        )}
      </div>

      {/* Search panel - left side */}
      <div style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        zIndex: 1000,
        width: '300px',
        maxHeight: 'calc(100vh - 20px)',
        overflowY: 'auto',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        border: '1px solid #eee'
      }}>
        {/* Input Section */}
        <div style={{
          padding: '15px',
          borderBottom: '1px solid #eee'
        }}>
          <form onSubmit={handleSearch}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>
                Search Location:
              </label>
              <div style={{ position: 'relative' }} ref={recentDropdownRef}>
                <input
                  ref={mainSearchRef}
                  type="text"
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    setRecentSearchTerm(e.target.value);
                    setShowRecentSearchesDropdown(true);
                    setHighlightedRecentIndex(-1);
                  }}
                  onFocus={() => {
                    setShowRecentSearchesDropdown(true);
                    setHighlightedRecentIndex(-1);
                  }}
                  onKeyDown={handleMainSearchKeyDown}
                  placeholder="Enter address or lat,lng to search"
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                
                {/* Recent Searches Dropdown */}
                {showRecentSearchesDropdown && filteredRecentSearches.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    maxHeight: '150px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    marginTop: '2px'
                  }}>
                    {filteredRecentSearches.map((historyInput, index) => {
                      const record = usageHistory
                        .filter(r => (historyInput.type === 'address' && r.address === historyInput.value) ||
                                     (historyInput.type === 'digipin' && r.digipin === historyInput.value))
                        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
                      
                      const safeAddress = typeof record?.address === 'string' ? record.address : historyInput.value;
                      
                      return (
                        <div
                          key={index}
                          onClick={() => selectRecentSearch(historyInput)}
                          style={{
                            padding: '8px',
                            cursor: 'pointer',
                            backgroundColor: index === highlightedRecentIndex ? '#f0f0f0' : 'transparent',
                            borderBottom: index < filteredRecentSearches.length - 1 ? '1px solid #eee' : 'none'
                          }}
                        >
                          <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{historyInput.value}</div>
                          <div style={{ fontSize: '11px', color: '#666' }}>{safeAddress}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Searching...' : 'Search Location'}
              </button>
              <button
                type="button"
                onClick={clearAll}
                style={{
                  padding: '10px 12px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
                title="Clear all"
              >
                Clear
              </button>
            </div>
          </form>
          
          {/* Map Layer Controls */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '13px' }}>
              Map View:
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {mapLayers && Object.keys(mapLayers).map((name) => (
                <button
                  key={name}
                  onClick={() => {
                    if (!map) return;
                    
                    console.log(`🗺️ Switching to ${name} layer`);
                    
                    // Remove current layers
                    if (currentLayer) {
                      if (typeof currentLayer === 'object' && currentLayer.type === 'single') {
                        // Handle single layer removal (both street and satellite are now single type)
                        if (currentLayer.base && map.hasLayer(currentLayer.base)) {
                          map.removeLayer(currentLayer.base);
                        }
                      } else if (map.hasLayer(currentLayer)) {
                        map.removeLayer(currentLayer);
                      }
                    }
                    
                    // Add new layer
                    const newLayer = mapLayers[name];
                    if (newLayer.type === 'single') {
                      // Handle single layer addition (both street and satellite)
                      if (newLayer.base) {
                        newLayer.base.addTo(map);
                      }
                    }
                    
                    setCurrentLayer(newLayer);
                    setCurrentLayerName(name);
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: currentLayerName === name ? '#007bff' : '#f8f9fa',
                    color: currentLayerName === name ? 'white' : '#333',
                    border: `1px solid ${currentLayerName === name ? '#007bff' : '#dee2e6'}`,
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}
                  title={`Switch to ${name}`}
                >
                  <span style={{ fontSize: '14px' }}>
                    {name === 'Street Map' && '🗺️'}
                    {name === 'Satellite' && '🛰️'}
                  </span>
                  <span>{name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Always Visible Specific Location Field */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>
              My Location:
            </label>
            <div style={{ position: 'relative' }} ref={specificDropdownRef}>
              <div style={{ position: 'relative' }}>
                <input
                  ref={specificLocationRef}
                  type="text"
                  value={specificLocationSearch}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setSpecificLocationSearch(newValue);
                    
                    // Show dropdown when field is cleared or has content
                    if (newValue.trim() === '') {
                      // Field is cleared - show all saved locations
                      setSpecificLocationDropdownOpen(true);
                      setHighlightedSpecificIndex(-1);
                    } else {
                      // Field has content - show filtered locations
                      setSpecificLocationDropdownOpen(true);
                      setHighlightedSpecificIndex(-1);
                    }
                  }}
                  onFocus={() => {
                    setSpecificLocationDropdownOpen(true);
                    setHighlightedSpecificIndex(-1);
                  }}
                  onKeyDown={handleSpecificLocationKeyDown}
                  placeholder="Search my locations..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    paddingRight: specificLocationSearch ? '35px' : '10px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                {specificLocationSearch && (
                  <button
                    type="button"
                    onClick={() => {
                      setSpecificLocationSearch('');
                      setSpecificLocationDropdownOpen(true);
                      setHighlightedSpecificIndex(-1);
                    }}
                    style={{
                      position: 'absolute',
                      right: '6px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      fontSize: '16px',
                      cursor: 'pointer',
                      color: '#999',
                      padding: '2px',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Clear field"
                  >
                    ×
                  </button>
                )}
              </div>
              
              {/* Specific Location Dropdown */}
              {specificLocationDropdownOpen && (
                <div style={{
                  position: 'fixed',
                  top: specificDropdownRef.current ? specificDropdownRef.current.getBoundingClientRect().bottom + 2 : 'auto',
                  left: specificDropdownRef.current ? specificDropdownRef.current.getBoundingClientRect().left : 'auto',
                  width: specificDropdownRef.current ? specificDropdownRef.current.offsetWidth : 'auto',
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  maxHeight: '250px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  {filteredSpecificAddresses.length > 0 ? (
                    filteredSpecificAddresses.map((addr, index) => (
                      <div
                        key={addr.id}
                        onClick={() => selectSpecificLocation(addr)}
                        style={{
                          padding: '8px',
                          cursor: 'pointer',
                          backgroundColor: index === highlightedSpecificIndex ? '#f0f0f0' : 'transparent',
                          borderBottom: index < filteredSpecificAddresses.length - 1 ? '1px solid #eee' : 'none'
                        }}
                      >
                        <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
                          {addr.label || addr.address}
                          {defaultSavedLocation && defaultSavedLocation.id === addr.id && (
                            <span style={{ color: '#28a745', fontSize: '11px', marginLeft: '6px' }}>
                              ✓ Default
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>{addr.address}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '8px', color: '#666', fontSize: '13px', textAlign: 'center' }}>
                      No saved locations found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showMyLocations}
                onChange={(e) => setShowMyLocations(e.target.checked)}
                style={{ margin: 0 }}
              />
              <span style={{ fontSize: '13px' }}>Show My Locations on Map</span>
            </label>
          </div>

          {/* De-Dup Control */}
          <DedupControl
            enableDedup={enableDedup}
            setEnableDedup={setEnableDedup}
            dedupLevel={dedupLevel}
            setDedupLevel={setDedupLevel}
            dedupData={dedupData}
            loading={dedupLoading}
            setDedupData={setDedupData}
            setDedupMarkers={setDedupMarkers}
            setSelectedDedupGroup={setSelectedDedupGroup}
            setShowDedupModal={setShowDedupModal}
            setDedupRectangles={setDedupRectangles}
            dedupRectangles={dedupRectangles}
            setDedupRectanglesKey={setDedupRectanglesKey}
            dedupRectanglesKey={dedupRectanglesKey}
            addressSimilarityThreshold={addressSimilarityThreshold}
            setAddressSimilarityThreshold={setAddressSimilarityThreshold}
          />

          {error && (
            <div style={{
              marginTop: '8px',
              padding: '8px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              color: '#721c24',
              fontSize: '12px'
            }}>
              {error}
            </div>
          )}

        </div>
        
      </div>

      {/* Location Source Message */}
      <LocationSourceMessage 
        locationSource={locationSource}
        onClose={() => setShowLocationMessage(false)}
      />

      {/* Add Location Modal */}
      <Modal 
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add to My Location"
      >
        <form onSubmit={handleAddAddress}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Input Type:
              </label>
              <select
                value={formData.inputType}
                onChange={(e) => setFormData(prev => ({ ...prev, inputType: e.target.value }))}
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="address">Address</option>
                <option value="latlng">Latitude, Longitude</option>
                <option value="digipin">Digipin</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                {formData.inputType === 'address' ? 'Address:' :
                 formData.inputType === 'latlng' ? 'Latitude, Longitude:' : 'Digipin:'}
              </label>
              <input
                type="text"
                value={formData.input}
                onChange={(e) => setFormData(prev => ({ ...prev, input: e.target.value }))}
                placeholder={
                  formData.inputType === 'address' ? 'Enter address...' :
                  formData.inputType === 'latlng' ? 'e.g., 28.6139,77.2090' : 'e.g., 4T3-7F2-2LJT'
                }
                required
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Label:
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                placeholder="Enter a label..."
                required
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.setAsDefault}
                  onChange={(e) => setFormData(prev => ({ ...prev, setAsDefault: e.target.checked }))}
                  style={{ 
                    width: '16px', 
                    height: '16px',
                    cursor: 'pointer'
                  }}
                />
                <span style={{ fontWeight: '500', fontSize: '14px' }}>
                  Set as default location
                </span>
              </label>
            </div>
          </div>
          
          {formMessage && (
            <div style={{ 
              marginTop: '16px', 
              padding: '8px', 
              backgroundColor: formMessage.includes('Error') ? '#f8d7da' : '#d4edda',
              border: `1px solid ${formMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`,
              borderRadius: '4px',
              color: formMessage.includes('Error') ? '#721c24' : '#155724',
              fontSize: '14px'
            }}>
              {formMessage}
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            <button 
              type="button"
              onClick={() => setShowAddModal(false)}
              style={{ 
                padding: '10px 20px', 
                background: '#6c757d', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={formLoading || !formData.input.trim() || !formData.label.trim()}
              style={{ 
                padding: '10px 20px', 
                background: '#28a745', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: formLoading ? 'not-allowed' : 'pointer',
                opacity: formLoading ? 0.6 : 1
              }}
            >
              {formLoading ? 'Saving...' : 'Add Location'}
            </button>
          </div>
        </form>
      </Modal>

      {/* De-Dup Transaction Modal */}
      <DedupTransactionModal
        isOpen={showDedupModal}
        onClose={() => setShowDedupModal(false)}
        groupData={selectedDedupGroup}
        dedupLevel={dedupLevel}
      />

      {/* Location Permission Prompt Modal */}
      {showLocationMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#3B82F6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '24px'
            }}>
              📍
            </div>
            
            <h3 style={{ margin: '0 0 16px 0', color: '#1f2937', fontSize: '18px', fontWeight: '600' }}>
              Location Access Required
            </h3>
            
            <p style={{ 
              margin: '0 0 24px 0', 
              color: '#6b7280', 
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              To view the Digipin of your location, please allow location sharing or set a default location.
            </p>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  console.log('🔵 Allow Location button clicked');
                  handleLocationPermissionGranted();
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2563EB';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#3B82F6';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <span className="material-icons" style={{ fontSize: '18px' }}>
                  my_location
                </span>
                Allow Location
              </button>
              
              <button
                onClick={() => {
                  console.log('🟢 Set Default Location button clicked');
                  handleSetDefaultLocation();
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#059669';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#10B981';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <span className="material-icons" style={{ fontSize: '18px' }}>
                  home
                </span>
                Set Default Location
              </button>
              
              <button
                onClick={() => {
                  console.log('⚫ Don\'t Allow button clicked');
                  handleLocationPermissionDenied();
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#4B5563';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#6B7280';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Don't Allow
              </button>
            </div>
            
            <p style={{ 
              margin: '16px 0 0 0', 
              color: '#9ca3af', 
              fontSize: '12px',
              lineHeight: '1.4'
            }}>
              Sharing your location will allow us to show you the Digipin of your exact location. 
              If you prefer not to share your exact location, we will show approximate location which might not be accurate. 
              You can always click anywhere on the map to find the Digipin of that location.
            </p>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#333',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '6px',
          fontSize: '14px',
          zIndex: 2000,
          animation: 'slideInUp 0.3s ease-out',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '90vw',
          minWidth: '200px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <span style={{ flex: 1 }}>{toastMessage}</span>
          <button
            onClick={dismissToast}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '3px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              lineHeight: 1,
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      
      <style>
        {`
          @keyframes slideInUp {
            from {
              transform: translate(-50%, 20px);
              opacity: 0;
            }
            to {
              transform: translate(-50%, 0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Pin2PointLeaflet;
